<template>
  <Loader v-if="loader" />
  <div class="card">
    <div class="excise-workspace">
      <h2 class="title">Visualizza Accise</h2>

      <div class="visual-filter-flow">
      <div class="selection-row">
        <div class="selection-group">
          <span class="selection-label">Materia</span>
          <SelectButton
            v-model="commodityTabIndex"
            :options="commodityOptions"
            optionLabel="label"
            optionValue="index"
            :allowEmpty="false"
            @change="onSelectionChange"
          />
        </div>
        <i v-if="commodityTabIndex !== null" class="pi pi-arrow-right selection-arrow" aria-hidden="true" />
        <div v-if="commodityTabIndex !== null" class="selection-group">
          <span class="selection-label">Periodo</span>
          <SelectButton
            v-model="periodTabIndex"
            :options="periodOptions"
            optionLabel="label"
            optionValue="index"
            :allowEmpty="false"
            @change="onPeriodChange"
          />
        </div>
        <i v-if="periodTabIndex !== null" class="pi pi-arrow-right selection-arrow" aria-hidden="true" />
        <div v-if="periodTabIndex !== null" class="selection-group">
          <span class="selection-label">Tipologia</span>
          <SelectButton
            v-model="operationTabIndex"
            :options="operationOptions"
            optionLabel="label"
            optionValue="index"
            :allowEmpty="false"
            @change="onOperationChange"
          />
        </div>
      </div>
      <i v-if="selectedOperation" class="pi pi-arrow-right selection-arrow" aria-hidden="true" />

      <div v-if="selectedOperation" class="search-panel">
        <div
          v-if="selectedPeriod === 'annuale' && selectedOperation === 'accise'"
          class="field search-field"
        >
          <label for="dogane-year">Anno</label>
          <Calendar
            id="dogane-year"
            v-model="filterYear"
            view="year"
            dateFormat="yy"
            :showIcon="true"
            :manualInput="false"
            :maxDate="currentYearMaxDate"
          />
        </div>

        <div
          v-else-if="selectedPeriod === 'annuale' && selectedOperation === 'consumi'"
          class="field search-field consumption-period-field"
        >
          <label for="dogane-consumption-period">Periodo da / a</label>
          <Calendar
            id="dogane-consumption-period"
            v-model="filterConsumptionPeriod"
            selectionMode="range"
            view="month"
            dateFormat="mm/yy"
            placeholder="Seleziona massimo 12 mesi"
            :showIcon="true"
            :manualInput="false"
            :hideOnRangeSelection="true"
            :maxDate="consumptionMaxDate"
          />
        </div>

        <div
          v-else-if="selectedPeriod === 'annuale' && selectedOperation === 'addizionali'"
          class="field search-field consumption-period-field"
        >
          <label for="dogane-addizionali-period">Periodo da / a</label>
          <Calendar
            id="dogane-addizionali-period"
            v-model="filterAddizionaliPeriod"
            selectionMode="range"
            view="month"
            dateFormat="mm/yy"
            placeholder="Seleziona massimo 12 mesi"
            :showIcon="true"
            :manualInput="false"
            :hideOnRangeSelection="true"
            :maxDate="addizionaliMaxDate"
          />
        </div>

        <template v-else-if="selectedPeriod === 'semestrale'">
          <div class="field search-field">
            <label for="dogane-semester-year">Anno</label>
            <Calendar
              id="dogane-semester-year"
              v-model="filterYear"
              view="year"
              dateFormat="yy"
              :showIcon="true"
              :manualInput="false"
              :maxDate="currentYearMaxDate"
            />
          </div>
          <div class="field search-field semester-field">
            <span class="field-label">Semestre</span>
            <div class="semester-options">
              <div v-for="option in semestreOptions" :key="option.value" class="semester-option">
                <RadioButton
                  v-model="filterSemester"
                  :inputId="`dogane-semester-${option.value}`"
                  name="dogane-semester"
                  :value="option.value"
                />
                <label :for="`dogane-semester-${option.value}`">{{ option.label }}</label>
              </div>
            </div>
          </div>
        </template>

        <div v-else class="field search-field">
          <label for="dogane-month">Anno e mese</label>
          <Calendar
            id="dogane-month"
            v-model="filterMonth"
            view="month"
            dateFormat="mm/yy"
            :showIcon="true"
            :manualInput="false"
            :maxDate="currentYearMaxDate"
          />
        </div>

        <div
          v-if="selectedPeriod === 'mensile' && selectedOperation === 'accise'"
          class="field search-field"
        >
          <label :for="commodityTabIndex === 0 ? 'dogane-ambito' : 'dogane-ambito-gas'">
            Ambito
          </label>
          <Dropdown
            v-if="commodityTabIndex === 0"
            id="dogane-ambito"
            v-model="selectedAmbitoEE"
            :options="ambitoOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Ambito"
          />
          <Dropdown
            v-else
            id="dogane-ambito-gas"
            v-model="selectedAmbitoGAS"
            :options="ambitoOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Ambito"
          />
        </div>

        <Button
          v-if="canSearch && !(selectedPeriod === 'semestrale' && selectedOperation === 'consumi')"
          label="Cerca"
          icon="pi pi-search"
          class="search-button"
          :loading="searching"
          @click="executeSearch"
        />

        <Button
          v-if="selectedPeriod === 'annuale' && selectedOperation === 'accise' && (showElettricoData || showGasData)"
          label="Esporta Excel"
          icon="pi pi-file-excel"
          class="p-button-success"
          @click="exportToExcel"
        />
      </div>
      </div>
    </div>

    <div v-if="showElettricoData">
      <h3 class="section-title">Statuti</h3>
      <DataTable
        :value="[
          ...elettricoStatuti,
          {
            statuto: 'Totali',
            mesi: Object.fromEntries(
              months.map((month) => [
                month.value,
                {
                  pods: calculateMonthlyTotal(
                    elettricoStatuti,
                    month.value,
                    'pods'
                  ),
                  rataReale: calculateMonthlyTotal(
                    elettricoStatuti,
                    month.value,
                    'rataReale'
                  ),
                },
              ])
            ),
            isTotalRow: false,
          },
          {
            statuto: 'Totale Progressivo',
            mesi: Object.fromEntries(
              months.map((month) => [
                month.value,
                {
                  rataReale: calculateProgressiveTotal(
                    months,
                    month.value,
                    elettricoStatuti
                  ),
                },
              ])
            ),
            isTotalProgressivo: true,
          },
        ]"
        scrollable
        scrollHeight="800px"
        showGridlines
        :empty-message="'Nessun dato disponibile per Elettrico - Statuti'"
        :rowClass="(data) => (data.isTotalRow ? 'total-row' : '')"
      >
        <Column field="statuto" header="Statuto" style="min-width: 200px">
          <template #body="{ data }">
            <div class="statuto-cell" :class="{ 'total-row': data.isTotalRow }">
              <strong>{{ data.statuto }}</strong>
              <br v-if="!data.isTotalRow" />
              <small v-if="!data.isTotalRow">{{ data.riferimento }}</small>
            </div>
          </template>
        </Column>
        <Column
          v-for="month in months"
          :key="month.value"
          :field="'mesi.' + month.value"
          :header="month.label"
          style="min-width: 75px; max-width: 85px; text-align: flex-start"
        >
          <template #body="{ data }">
            <div class="month-data">
              <div v-if="!data.isTotalProgressivo" class="data-row">
                <span class="label">
                  {{ data.isTotalRow ? "TOT POD:" : "POD:" }}
                </span>
                <span class="value">
                  {{ data.mesi[month.value.padStart(2, "0")]?.pods || 0 }}
                </span>
              </div>
              <div class="data-row">
                <span class="label">
                  {{ data.isTotalRow ? "TOT Accise:" : "Accise:" }}
                </span>
                <span class="value">
                  {{
                    data.isTotalProgressivo
                      ? getPreviousMonthAccise(data.mesi, month.value) !==
                        data.mesi[month.value.padStart(2, "0")]?.rataReale
                        ? formatCurrency(
                            data.mesi[month.value.padStart(2, "0")]
                              ?.rataReale || 0
                          )
                        : ""
                      : formatCurrency(
                          data.mesi[month.value.padStart(2, "0")]?.rataReale ||
                            0
                        )
                  }}
                </span>
              </div>
              <Button
                v-if="
                  !data.isTotalRow &&
                  data.mesi[month.value.padStart(2, '0')]?.pods > 0
                "
                icon="pi pi-download"
                class="p-button-text p-button-sm download-btn"
                style="margin-top: 1rem"
                @click="downloadCsv(data, month.value)"
              />
            </div>
          </template>
        </Column>
        <Column field="conguaglio" header="Conguaglio">
          <template #body="slotProps">
            <div
              v-if="
                slotProps.data.statuto === 'Totali' ||
                slotProps.data.statuto === 'Totale Progressivo'
              "
              class="conguaglio-cell"
            >
              <span class="conguaglio-value">
                {{ formatCurrency(getTotalConguaglio(false)) }}
              </span>
            </div>
            <div v-else class="conguaglio-cell">
              <span class="conguaglio-value">
                {{
                  formatCurrency(
                    slotProps.data.conguaglio?.acciseFatturate || 0
                  )
                }}
              </span>
            </div>
          </template>
        </Column>
      </DataTable>

      <h3 class="section-title">Province</h3>
      <DataTable
        :value="[
          ...elettricoProvince,
          {
            statuto: 'Totali',
            mesi: Object.fromEntries(
              months.map((month) => [
                month.value,
                {
                  pods: calculateMonthlyTotal(
                    elettricoProvince,
                    month.value,
                    'pods'
                  ),
                  rataReale: calculateMonthlyTotal(
                    elettricoProvince,
                    month.value,
                    'rataReale'
                  ),
                },
              ])
            ),
            isTotalRow: false,
          },
          {
            statuto: 'Totale Progressivo',
            mesi: Object.fromEntries(
              months.map((month) => [
                month.value,
                {
                  rataReale: calculateProgressiveTotal(
                    months,
                    month.value,
                    elettricoProvince
                  ),
                },
              ])
            ),
            isTotalProgressivo: true,
          },
        ]"
        scrollable
        scrollHeight="800px"
        showGridlines
        :empty-message="'Nessun dato disponibile per Elettrico - Province'"
        :rowClass="(data) => (data.isTotalRow ? 'total-row' : '')"
      >
        <Column field="statuto" header="Provincia" style="min-width: 200px">
          <template #body="{ data }">
            <div class="statuto-cell" :class="{ 'total-row': data.isTotalRow }">
              <strong>{{ data.statuto }}</strong>
              <br v-if="!data.isTotalRow && !data.isTotalProgressivo" />
              <small v-if="!data.isTotalRow && !data.isTotalProgressivo">{{
                data.riferimento
              }}</small>
            </div>
          </template>
        </Column>
        <Column
          v-for="month in months"
          :key="month.value"
          :field="'mesi.' + month.value"
          :header="month.label"
          style="min-width: 75px; max-width: 85px; text-align: flex-start"
        >
          <template #body="{ data }">
            <div class="month-data">
              <div v-if="!data.isTotalProgressivo" class="data-row">
                <span
                  class="label"
                  :class="{
                    'label-white':
                      (!data.isTotalRow &&
                        isEntryMonth(data.statuto, month.value)) ||
                      isExitMonth(data.statuto, month.value),
                  }"
                >
                  {{ data.isTotalRow ? "TOT POD:" : "POD:" }}
                </span>
                <span
                  class="value"
                  :class="{
                    'value-green':
                      !data.isTotalRow &&
                      isEntryMonth(data.statuto, month.value),
                    'value-red':
                      !data.isTotalRow &&
                      isExitMonth(data.statuto, month.value),
                  }"
                >
                  {{ data.mesi[month.value.padStart(2, "0")]?.pods || 0 }}
                </span>
              </div>
              <div class="data-row">
                <span
                  class="label"
                  :class="{
                    'label-white':
                      (!data.isTotalRow &&
                        isEntryMonth(data.statuto, month.value)) ||
                      isExitMonth(data.statuto, month.value),
                  }"
                >
                  {{ data.isTotalRow ? "TOT Accise:" : "Accise:" }}
                </span>
                <span
                  class="value"
                  :class="{
                    'value-green':
                      !data.isTotalRow &&
                      isEntryMonth(data.statuto, month.value),
                    'value-red':
                      !data.isTotalRow &&
                      isExitMonth(data.statuto, month.value),
                  }"
                >
                  {{
                    data.isTotalProgressivo
                      ? getPreviousMonthAccise(data.mesi, month.value) !==
                        data.mesi[month.value.padStart(2, "0")]?.rataReale
                        ? formatCurrency(
                            data.mesi[month.value.padStart(2, "0")]
                              ?.rataReale || 0
                          )
                        : ""
                      : formatCurrency(
                          data.mesi[month.value.padStart(2, "0")]?.rataReale ||
                            0
                        )
                  }}
                </span>
              </div>
              <Button
                v-if="
                  !data.isTotalRow &&
                  data.mesi[month.value.padStart(2, '0')]?.pods > 0
                "
                icon="pi pi-download"
                class="p-button-text p-button-sm download-btn"
                style="margin-top: 1rem"
                @click="downloadCsv(data, month.value)"
              />
            </div>
          </template>
        </Column>
        <Column field="conguaglio" header="Conguaglio">
          <template #body="slotProps">
            <div
              v-if="
                slotProps.data.statuto === 'Totali' ||
                slotProps.data.statuto === 'Totale Progressivo'
              "
              class="conguaglio-cell"
            >
              <span class="conguaglio-value">
                {{ formatCurrency(getTotalConguaglio(true)) }}
              </span>
            </div>
            <div v-else class="conguaglio-cell">
              <span class="conguaglio-value">
                {{
                  formatCurrency(
                    slotProps.data.conguaglio?.acciseFatturate || 0
                  )
                }}
              </span>
            </div>
          </template>
        </Column>
      </DataTable>
    </div>

    <div v-if="showGasData">
      <h3 class="section-title">Statuti</h3>
      <DataTable
        :value="[
          ...gasStatuti,
          {
            statuto: 'Totali',
            mesi: Object.fromEntries(
              months.map((month) => [
                month.value,
                {
                  pdrs: calculateMonthlyTotal(gasStatuti, month.value, 'pdrs'),
                  rataReale: calculateMonthlyTotal(
                    gasStatuti,
                    month.value,
                    'rataReale'
                  ),
                },
              ])
            ),

            isTotalRow: false,
          },
          {
            statuto: 'Totale Progressivo',

            mesi: Object.fromEntries(
              months.map((month) => [
                month.value,

                {
                  rataReale: calculateProgressiveTotal(
                    months,

                    month.value,

                    gasStatuti
                  ),
                },
              ])
            ),

            isTotalProgressivo: true,
          },
        ]"
        scrollable
        scrollHeight="800px"
        showGridlines
        :empty-message="'Nessun dato disponibile per Gas - Statuti'"
        :rowClass="(data) => (data.isTotalRow ? 'total-row' : '')"
      >
        <Column field="statuto" header="Statuto" style="min-width: 200px">
          <template #body="{ data }">
            <div class="statuto-cell" :class="{ 'total-row': data.isTotalRow }">
              <strong>{{ data.statuto }}</strong>

              <br v-if="!data.isTotalRow" />

              <small v-if="!data.isTotalRow">{{ data.riferimento }}</small>
            </div>
          </template>
        </Column>

        <Column
          v-for="month in months"
          :key="month.value"
          :field="'mesi.' + month.value"
          :header="month.label"
          style="min-width: 75px; max-width: 85px; text-align: flex-start"
        >
          <template #body="{ data }">
            <div class="month-data">
              <div v-if="!data.isTotalProgressivo" class="data-row">
                <span class="label">
                  {{ data.isTotalRow ? "TOT PDR:" : "PDR:" }}
                </span>

                <span class="value">
                  {{ data.mesi[month.value.padStart(2, "0")]?.pdrs || 0 }}
                </span>
              </div>

              <div class="data-row">
                <span class="label">
                  {{ data.isTotalRow ? "TOT Accise:" : "Accise:" }}
                </span>

                <span class="value">
                  {{
                    data.isTotalProgressivo
                      ? getPreviousMonthAccise(data.mesi, month.value) !==
                        data.mesi[month.value.padStart(2, "0")]?.rataReale
                        ? formatCurrency(
                            data.mesi[month.value.padStart(2, "0")]
                              ?.rataReale || 0
                          )
                        : ""
                      : formatCurrency(
                          data.mesi[month.value.padStart(2, "0")]?.rataReale ||
                            0
                        )
                  }}
                </span>
              </div>

              <Button
                v-if="
                  !data.isTotalRow &&
                  !data.isTotalProgressivo &&
                  data.mesi[month.value.padStart(2, '0')]?.pdrs > 0
                "
                icon="pi pi-download"
                class="p-button-text p-button-sm download-btn"
                style="margin-top: 1rem"
                @click="downloadGasCsv(data, month.value)"
              />
            </div>
          </template>
        </Column>

        <Column field="conguaglio" header="Conguaglio">
          <template #body="slotProps">
            <div
              v-if="
                slotProps.data.statuto === 'Totali' ||
                slotProps.data.statuto === 'Totale Progressivo'
              "
              class="conguaglio-cell"
            >
              <span class="conguaglio-value">
                {{ formatCurrency(getTotalConguaglio(false)) }}
              </span>
            </div>

            <div v-else class="conguaglio-cell">
              <span class="conguaglio-value">
                {{
                  formatCurrency(
                    slotProps.data.conguaglio?.acciseFatturate || 0
                  )
                }}
              </span>
            </div>
          </template>
        </Column>
      </DataTable>

      <h3 class="section-title">Province</h3>
      <DataTable
        :value="[
          ...gasProvince,
          {
            statuto: 'Totali',
            mesi: Object.fromEntries(
              months.map((month) => [
                month.value,
                {
                  pods: calculateMonthlyTotal(
                    gasProvince,
                    month.value,
                    'pods'
                  ),
                  rataReale: calculateMonthlyTotal(
                    gasProvince,
                    month.value,
                    'rataReale'
                  ),
                },
              ])
            ),
            isTotalRow: false,
          },
          {
            statuto: 'Totale Progressivo',
            mesi: Object.fromEntries(
              months.map((month) => [
                month.value,
                {
                  rataReale: calculateProgressiveTotal(
                    months,
                    month.value,
                    gasProvince
                  ),
                },
              ])
            ),
            isTotalProgressivo: true,
          },
        ]"
        scrollable
        scrollHeight="800px"
        showGridlines
        :empty-message="'Nessun dato disponibile per Gas - Province'"
        :rowClass="(data) => (data.isTotalRow ? 'total-row' : '')"
      >
        <Column field="statuto" header="Provincia" style="min-width: 200px">
          <template #body="{ data }">
            <div class="statuto-cell" :class="{ 'total-row': data.isTotalRow }">
              <strong>{{ data.statuto }}</strong>
              <br v-if="!data.isTotalRow" />
              <small v-if="!data.isTotalRow">{{ data.riferimento }}</small>
            </div>
          </template>
        </Column>
        <Column
          v-for="month in months"
          :key="month.value"
          :field="'mesi.' + month.value"
          :header="month.label"
          style="min-width: 75px; max-width: 85px; text-align: flex-start"
        >
          <template #body="{ data }">
            <div class="month-data">
              <div v-if="!data.isTotalProgressivo" class="data-row">
                <span
                  class="label"
                  :class="{
                    'label-white':
                      !data.isTotalRow &&
                      (isEntryMonthGas(data.statuto, month.value) ||
                        isExitMonthGas(data.statuto, month.value)),
                  }"
                >
                  {{ data.isTotalRow ? "TOT PDR:" : "PDR:" }}
                </span>
                <span
                  class="value"
                  :class="{
                    'value-green':
                      !data.isTotalRow &&
                      isEntryMonthGas(data.statuto, month.value),
                    'value-red':
                      !data.isTotalRow &&
                      isExitMonthGas(data.statuto, month.value),
                  }"
                >
                  {{ data.mesi[month.value.padStart(2, "0")]?.pdrs || 0 }}
                </span>
              </div>
              <div class="data-row">
                <span
                  class="label"
                  :class="{
                    'label-white':
                      !data.isTotalRow &&
                      (isEntryMonthGas(data.statuto, month.value) ||
                        isExitMonthGas(data.statuto, month.value)),
                  }"
                >
                  {{ data.isTotalRow ? "TOT Accise:" : "Accise:" }}
                </span>
                <span
                  class="value"
                  :class="{
                    'value-green':
                      !data.isTotalRow &&
                      isEntryMonthGas(data.statuto, month.value),
                    'value-red':
                      !data.isTotalRow &&
                      isExitMonthGas(data.statuto, month.value),
                  }"
                >
                  {{
                    data.isTotalProgressivo
                      ? getPreviousMonthAccise(data.mesi, month.value) !==
                        data.mesi[month.value.padStart(2, "0")]?.rataReale
                        ? formatCurrency(
                            data.mesi[month.value.padStart(2, "0")]
                              ?.rataReale || 0
                          )
                        : ""
                      : formatCurrency(
                          data.mesi[month.value.padStart(2, "0")]?.rataReale ||
                            0
                        )
                  }}
                </span>
              </div>
              <Button
                v-if="
                  !data.isTotalRow &&
                  !data.isTotalProgressivo &&
                  data.mesi[month.value.padStart(2, '0')]?.pdrs > 0
                "
                icon="pi pi-download"
                class="p-button-text p-button-sm download-btn"
                style="margin-top: 1rem"
                @click="downloadGasCsv(data, month.value)"
              />
            </div>
          </template>
        </Column>
        <Column field="conguaglio" header="Conguaglio">
          <template #body="slotProps">
            <div
              v-if="
                slotProps.data.statuto === 'Totali' ||
                slotProps.data.statuto === 'Totale Progressivo'
              "
              class="conguaglio-cell"
            >
              <span class="conguaglio-value">
                {{ formatCurrency(getTotalConguaglio(true)) }}
              </span>
            </div>
            <div v-else class="conguaglio-cell">
              <span class="conguaglio-value">
                {{
                  formatCurrency(
                    slotProps.data.conguaglio?.acciseFatturate || 0
                  )
                }}
              </span>
            </div>
          </template>
        </Column>
      </DataTable>
    </div>

    <section v-if="showMensileEE" class="inline-declaration-section">
      <h3 class="section-title">Mensile EE</h3>
      <div class="mensile-ee-container declaration-content">
        <template v-if="loading">
          <div
            class="loading-indicator"
            style="
              display: flex;
              justify-content: center;
              align-items: center;
              height: 200px;
            "
          >
            <ProgressSpinner style="width: 50px; height: 50px" />
            <span style="margin-left: 1rem">Caricamento dati in corso...</span>
          </div>
        </template>

        <template v-else-if="fasceArray && fasceArray.length > 0">
          <DataTable
            :value="fasceArray"
            class="p-mt-4"
            :rowHover="true"
            responsiveLayout="scroll"
          >
            <Column field="codice" header="Codice Rigo" />
            <Column field="pod" header="Numero POD" />
            <Column header="Quota Parte del Numero di POD">
              <template #body="slotProps">
                {{ calculateQuotaPod(slotProps.data.pod) }}%
              </template>
            </Column>
            <Column header="Quantità kWh">
              <template #body="slotProps">
                {{ formatNumber(slotProps.data.consumo) }}
              </template>
            </Column>
            <Column header="Quota Parte Quantità kWh">
              <template #body="slotProps">
                {{ calculateQuotaConsumo(slotProps.data.consumo) }}%
              </template>
            </Column>
            <Column header="Numero Fatture">
              <template #body="slotProps">
                {{ formatNumber(slotProps.data.fatture) }}
              </template>
            </Column>
            <Column header="Accise">
              <template #body="slotProps">
                {{ formatCurrency(slotProps.data.accise) }}
              </template>
            </Column>
          </DataTable>

          <div class="totals p-mt-3">
            <strong>Totali:</strong>
            <div>Numero POD: {{ formatNumber(totali.totPod) }}</div>
            <div>Quantità kWh: {{ formatNumber(totali.totConsumo) }}</div>
            <div>Numero Fatture: {{ formatNumber(totali.totFatture) }}</div>
            <div>Accise: {{ formatCurrency(totali.totAccise) }}</div>
          </div>
        </template>

        <template v-else>
          <div
            class="loading-indicator"
            style="
              display: flex;
              justify-content: center;
              align-items: center;
              height: 200px;
            "
          >
            <ProgressSpinner style="width: 50px; height: 50px" />
            <span style="margin-left: 1rem">Caricamento dati in corso...</span>
          </div>
        </template>
      </div>
    </section>

    <section v-if="showMensileGAS" class="inline-declaration-section">
      <h3 class="section-title">Mensile GAS</h3>
      <div class="mensile-gas-container declaration-content">
        <template v-if="loadingGAS">
          <div
            class="loading-indicator"
            style="
              display: flex;
              justify-content: center;
              align-items: center;
              height: 200px;
            "
          >
            <ProgressSpinner style="width: 50px; height: 50px" />
            <span style="margin-left: 1rem">Caricamento dati in corso...</span>
          </div>
        </template>

        <template v-else-if="fasceArrayGAS && fasceArrayGAS.length > 0">
          <DataTable
            :value="fasceArrayGAS"
            class="p-mt-4"
            :rowHover="true"
            responsiveLayout="scroll"
          >
            <Column field="codice" header="Codice Rigo" />
            <Column field="pdr" header="Numero PDR" />
            <Column header="Quota Parte del Numero di PDR">
              <template #body="slotProps">
                {{ calculateQuotaPdrGAS(slotProps.data.pdr) }}%
              </template>
            </Column>
            <Column header="Quantità Smc">
              <template #body="slotProps">
                {{ formatNumber(slotProps.data.consumo) }}
              </template>
            </Column>
            <Column header="Quota Parte Quantità Smc">
              <template #body="slotProps">
                {{ calculateQuotaConsumoGAS(slotProps.data.consumo) }}%
              </template>
            </Column>
            <Column header="Numero Fatture">
              <template #body="slotProps">
                {{ formatNumber(slotProps.data.fatture) }}
              </template>
            </Column>
            <Column header="Accise">
              <template #body="slotProps">
                {{ formatCurrency(slotProps.data.accise) }}
              </template>
            </Column>
          </DataTable>

          <div class="totals p-mt-3">
            <strong>Totali:</strong>
            <div>Numero PDR: {{ formatNumber(totaliGAS.totPdr) }}</div>
            <div>Quantità Smc: {{ formatNumber(totaliGAS.totConsumo) }}</div>
            <div>Numero Fatture: {{ formatNumber(totaliGAS.totFatture) }}</div>
            <div>Accise: {{ formatCurrency(totaliGAS.totAccise) }}</div>
          </div>
        </template>

        <template v-else>
          <div class="no-data" style="text-align: center; padding: 2rem">
            Nessun dato disponibile
          </div>
        </template>
      </div>
    </section>

    <section v-if="showDichiarazioneEE" class="inline-declaration-section">
      <h3 class="section-title">Consumi Annuali EE</h3>
      <div class="dichiarazione-ee-container declaration-content">
        <div v-if="hasDichiarazioneData" class="result-actions">
            <Button
              label="Esporta CSV"
              icon="pi pi-download"
              @click="downloadDichiarazioneCsv"
              class="p-button-success"
            />
        </div>

        <div v-if="dichiarazioneLoading" class="loading-indicator">
          <ProgressSpinner />
          <span>Caricamento dati in corso...</span>
        </div>

        <div v-else-if="hasDichiarazioneData">
          <h3>Domestici Residenti</h3>
          <DataTable
            :value="[
              ...dichiarazioneData.domesticiResidenti,
              {
                comune: 'TOTALE',
                catastale: '-',
                provincia: '-',
                qta150: dichiarazioneData.domesticiResidenti.reduce(
                  (acc, curr) => acc + (parseFloat(curr.qta150) || 0),
                  0
                ),
                qtao150: dichiarazioneData.domesticiResidenti.reduce(
                  (acc, curr) => acc + (parseFloat(curr.qtao150) || 0),
                  0
                ),
                nmin150: dichiarazioneData.domesticiResidenti.reduce(
                  (acc, curr) => acc + (parseInt(curr.nmin150) || 0),
                  0
                ),
                nmax150: dichiarazioneData.domesticiResidenti.reduce(
                  (acc, curr) => acc + (parseInt(curr.nmax150) || 0),
                  0
                ),
                distributore: '-',
                isTotalRow: false,
              },
            ]"
            scrollable
            scrollHeight="400px"
            showGridlines
          >
            <Column field="comune" header="Comune">
              <template #body="slotProps">
                <div :class="{ 'total-row': slotProps.data.isTotalRow }">
                  {{ slotProps.data.comune }}
                </div>
              </template>
            </Column>
            <Column field="catastale" header="Codice Catastale">
              <template #body="slotProps">
                {{ slotProps.data.catastale }}
              </template>
            </Column>
            <Column field="provincia" header="Provincia">
              <template #body="slotProps">
                {{ slotProps.data.provincia }}
              </template>
            </Column>
            <Column field="qta150" header="Fino a 150 kWh">
              <template #body="slotProps">
                <div :class="{ 'total-row': slotProps.data.isTotalRow }">
                  {{ formatNumber(slotProps.data.qta150) }}
                </div>
              </template>
            </Column>
            <Column field="qtao150" header="Oltre 150 kWh">
              <template #body="slotProps">
                <div :class="{ 'total-row': slotProps.data.isTotalRow }">
                  {{ formatNumber(slotProps.data.qtao150) }}
                </div>
              </template>
            </Column>
            <Column field="nmin150" header="Punti 0-150">
              <template #body="slotProps">
                <div :class="{ 'total-row': slotProps.data.isTotalRow }">
                  {{ formatNumber(slotProps.data.nmin150) }}
                </div>
              </template>
            </Column>
            <Column field="nmax150" header="Punti > 150">
              <template #body="slotProps">
                <div :class="{ 'total-row': slotProps.data.isTotalRow }">
                  {{ formatNumber(slotProps.data.nmax150) }}
                </div>
              </template>
            </Column>
            <Column field="distributore" header="Distributore">
              <template #body="slotProps">
                {{ slotProps.data.distributore }}
              </template>
            </Column>
          </DataTable>

          <h3 class="mt-4">Domestici Non Residenti</h3>
          <DataTable
            :value="[
              ...dichiarazioneData.domesticiNonResidenti,
              {
                comune: 'TOTALE',
                catastale: '-',
                provincia: '-',
                qta: dichiarazioneData.domesticiNonResidenti.reduce(
                  (acc, curr) => acc + (parseFloat(curr.qta) || 0),
                  0
                ),
                npunti: dichiarazioneData.domesticiNonResidenti.reduce(
                  (acc, curr) => acc + (parseInt(curr.npunti) || 0),
                  0
                ),
                distributore: '-',
                isTotalRow: false,
              },
            ]"
            scrollable
            scrollHeight="400px"
            showGridlines
          >
            <Column field="comune" header="Comune">
              <template #body="slotProps">
                <div :class="{ 'total-row': slotProps.data.isTotalRow }">
                  {{ slotProps.data.comune }}
                </div>
              </template>
            </Column>
            <Column field="catastale" header="Codice Catastale">
              <template #body="slotProps">
                {{ slotProps.data.catastale }}
              </template>
            </Column>
            <Column field="provincia" header="Provincia">
              <template #body="slotProps">
                {{ slotProps.data.provincia }}
              </template>
            </Column>
            <Column field="qta" header="Energia">
              <template #body="slotProps">
                <div :class="{ 'total-row': slotProps.data.isTotalRow }">
                  {{ formatNumber(slotProps.data.qta) }}
                </div>
              </template>
            </Column>
            <Column field="npunti" header="Punti">
              <template #body="slotProps">
                <div :class="{ 'total-row': slotProps.data.isTotalRow }">
                  {{ formatNumber(slotProps.data.npunti) }}
                </div>
              </template>
            </Column>
            <Column field="distributore" header="Distributore">
              <template #body="slotProps">
                {{ slotProps.data.distributore }}
              </template>
            </Column>
          </DataTable>

          <h3 class="mt-4">Altri Usi</h3>
          <DataTable
            :value="[
              ...dichiarazioneData.altriUsi,
              {
                comune: 'TOTALE',
                catastale: '-',
                provincia: '-',
                qta200: dichiarazioneData.altriUsi.reduce(
                  (acc, curr) => acc + (parseFloat(curr.qta200) || 0),
                  0
                ),
                qtao200: dichiarazioneData.altriUsi.reduce(
                  (acc, curr) => acc + (parseFloat(curr.qtao200) || 0),
                  0
                ),
                nmin200: dichiarazioneData.altriUsi.reduce(
                  (acc, curr) => acc + (parseInt(curr.nmin200) || 0),
                  0
                ),
                nmax200: dichiarazioneData.altriUsi.reduce(
                  (acc, curr) => acc + (parseInt(curr.nmax200) || 0),
                  0
                ),
                distributore: '-',
                isTotalRow: false,
              },
            ]"
            scrollable
            scrollHeight="400px"
            showGridlines
          >
            <Column field="comune" header="Comune">
              <template #body="slotProps">
                <div :class="{ 'total-row': slotProps.data.isTotalRow }">
                  {{ slotProps.data.comune }}
                </div>
              </template>
            </Column>
            <Column field="catastale" header="Codice Catastale">
              <template #body="slotProps">
                {{ slotProps.data.catastale }}
              </template>
            </Column>
            <Column field="provincia" header="Provincia">
              <template #body="slotProps">
                {{ slotProps.data.provincia }}
              </template>
            </Column>
            <Column field="qta200" header="Fino a 200k">
              <template #body="slotProps">
                <div :class="{ 'total-row': slotProps.data.isTotalRow }">
                  {{ formatNumber(slotProps.data.qta200) }}
                </div>
              </template>
            </Column>
            <Column field="qtao200" header="Oltre 200k">
              <template #body="slotProps">
                <div :class="{ 'total-row': slotProps.data.isTotalRow }">
                  {{ formatNumber(slotProps.data.qtao200) }}
                </div>
              </template>
            </Column>
            <Column field="nmin200" header="Punti 0-200k">
              <template #body="slotProps">
                <div :class="{ 'total-row': slotProps.data.isTotalRow }">
                  {{ formatNumber(slotProps.data.nmin200) }}
                </div>
              </template>
            </Column>
            <Column field="nmax200" header="Punti > 200k">
              <template #body="slotProps">
                <div :class="{ 'total-row': slotProps.data.isTotalRow }">
                  {{ formatNumber(slotProps.data.nmax200) }}
                </div>
              </template>
            </Column>
            <Column field="distributore" header="Distributore">
              <template #body="slotProps">
                {{ slotProps.data.distributore }}
              </template>
            </Column>
          </DataTable>

          <h3 class="mt-4">Totale per Reseller</h3>
          <div class="totali-table p-grid">
            <div class="p-col-12">
              <div class="grid">
                <div class="col-6 font-bold">Ragione Sociale</div>
                <div class="col-6 font-bold">Totale kWh</div>
              </div>
              <div
                class="grid"
                v-for="reseller in dichiarazioneData.totaleReseller"
                :key="reseller.ragsoc"
              >
                <div class="col-6">{{ reseller.ragsoc }}</div>
                <div class="col-6">{{ formatNumber(reseller.totale) }}</div>
              </div>
            </div>
          </div>

          <h3 class="mt-4">Riepilogo Totali</h3>
          <DataTable
            :value="[
              {
                totaleDomesticiResidenti:
                  dichiarazioneData.riepilogo.totaleDomesticiResidenti,
                totaleDomesticiResidentiEsenti:
                  dichiarazioneData.domesticiResidenti.reduce(
                    (acc, curr) => acc + (parseFloat(curr.qta150) || 0),
                    0
                  ),
                totaleDomesticiResidentiNonEsenti:
                  dichiarazioneData.domesticiResidenti.reduce(
                    (acc, curr) => acc + (parseFloat(curr.qtao150) || 0),
                    0
                  ),
                totaleDomesticiNonResidenti:
                  dichiarazioneData.riepilogo.totaleDomesticiNonResidenti,
                totaleAltriUsi: dichiarazioneData.riepilogo.totaleAltriUsi,
                totaleReseller: dichiarazioneData.riepilogo.totaleReseller,
                totaleComplessivo:
                  dichiarazioneData.riepilogo.totaleComplessivo,
              },
            ]"
            class="totali-table"
            showGridlines
          >
            <Column
              field="totaleDomesticiResidenti"
              header="Totale Domestici Residenti"
            >
              <template #body="slotProps">
                {{ formatNumber(slotProps.data.totaleDomesticiResidenti) }} kWh
              </template>
            </Column>
            <Column
              field="totaleDomesticiResidentiEsenti"
              header="Totale Domestici Residenti Esenti"
            >
              <template #body="slotProps">
                {{
                  formatNumber(slotProps.data.totaleDomesticiResidentiEsenti)
                }}
                kWh
              </template>
            </Column>
            <Column
              field="totaleDomesticiResidentiNonEsenti"
              header="Totale Domestici Residenti Non Esenti"
            >
              <template #body="slotProps">
                {{
                  formatNumber(slotProps.data.totaleDomesticiResidentiNonEsenti)
                }}
                kWh
              </template>
            </Column>
            <Column
              field="totaleDomesticiNonResidenti"
              header="Totale Domestici Non Residenti"
            >
              <template #body="slotProps">
                {{ formatNumber(slotProps.data.totaleDomesticiNonResidenti) }}
                kWh
              </template>
            </Column>
            <Column field="totaleAltriUsi" header="Totale Altri Usi">
              <template #body="slotProps">
                {{ formatNumber(slotProps.data.totaleAltriUsi) }} kWh
              </template>
            </Column>
            <Column field="totaleReseller" header="Totale Reseller">
              <template #body="slotProps">
                {{ formatNumber(slotProps.data.totaleReseller) }} kWh
              </template>
            </Column>
            <Column field="complessivo" header="Totale Complessivo">
              <template #body="slotProps">
                {{
                  formatNumber(
                    dichiarazioneData.riepilogo.totaleComplessivo ||
                      slotProps.data.complessivo
                  )
                }}
                kWh
              </template>
            </Column>
          </DataTable>
        </div>

        <!-- <div v-else class="no-data-message">
          Nessun dato disponibile per il periodo selezionato
        </div> -->
      </div>
    </section>

    <section v-if="showDichiarazioneGAS" class="inline-declaration-section">
      <h3 class="section-title">Consumi Annuali GAS</h3>
      <div class="dichiarazione-gas-container declaration-content">
        <div v-if="hasDichiarazioneGasData" class="result-actions">
            <Button
              label="Esporta CSV"
              icon="pi pi-download"
              @click="downloadDichiarazioneGasCsv"
              class="p-button-success"
            />
        </div>

        <div v-if="dichiarazioneGasLoading" class="loading-indicator">
          <ProgressSpinner />
          <span>Caricamento dati in corso...</span>
        </div>

        <div v-else-if="dichiarazioneGasData">
          <h3>Domestici Mezzogiorno</h3>
          <DataTable
            :value="[
              ...Object.values(dichiarazioneGasData.domesticiMezzogiorno || {}),
              {
                comune: 'TOTALE',
                catastale: '-',
                provincia: '-',
                qta120: Object.values(
                  dichiarazioneGasData.domesticiMezzogiorno || {}
                ).reduce(
                  (acc, curr) => acc + (parseFloat(curr.qta120) || 0),
                  0
                ),
                qta480: Object.values(
                  dichiarazioneGasData.domesticiMezzogiorno || {}
                ).reduce(
                  (acc, curr) => acc + (parseFloat(curr.qta480) || 0),
                  0
                ),
                qta1560: Object.values(
                  dichiarazioneGasData.domesticiMezzogiorno || {}
                ).reduce(
                  (acc, curr) => acc + (parseFloat(curr.qta1560) || 0),
                  0
                ),
                qtao1560: Object.values(
                  dichiarazioneGasData.domesticiMezzogiorno || {}
                ).reduce(
                  (acc, curr) => acc + (parseFloat(curr.qtao1560) || 0),
                  0
                ),
                n120: Object.values(
                  dichiarazioneGasData.domesticiMezzogiorno || {}
                ).reduce((acc, curr) => acc + (parseInt(curr.n120) || 0), 0),
                n480: Object.values(
                  dichiarazioneGasData.domesticiMezzogiorno || {}
                ).reduce((acc, curr) => acc + (parseInt(curr.n480) || 0), 0),
                n1560: Object.values(
                  dichiarazioneGasData.domesticiMezzogiorno || {}
                ).reduce((acc, curr) => acc + (parseInt(curr.n1560) || 0), 0),
                no1560: Object.values(
                  dichiarazioneGasData.domesticiMezzogiorno || {}
                ).reduce((acc, curr) => acc + (parseInt(curr.no1560) || 0), 0),
                distributore: '-',
                distributorePiva: '-',
                isTotalRow: false,
              },
            ]"
            scrollable
            scrollHeight="400px"
            showGridlines
          >
            <Column field="comune" header="Comune">
              <template #body="slotProps">
                <div :class="{ 'total-row': slotProps.data.isTotalRow }">
                  {{ slotProps.data.comune }}
                </div>
              </template>
            </Column>
            <Column field="catastale" header="Codice Catastale">
              <template #body="slotProps">
                {{ slotProps.data.catastale }}
              </template>
            </Column>
            <Column field="provincia" header="Provincia">
              <template #body="slotProps">
                {{ slotProps.data.provincia }}
              </template>
            </Column>
            <Column field="qta120" header="Fino a 120">
              <template #body="slotProps">
                <div :class="{ 'total-row': slotProps.data.isTotalRow }">
                  {{ formatNumber(slotProps.data.qta120) }}
                </div>
              </template>
            </Column>
            <Column field="qta480" header="Da 120 a 480">
              <template #body="slotProps">
                <div :class="{ 'total-row': slotProps.data.isTotalRow }">
                  {{ formatNumber(slotProps.data.qta480) }}
                </div>
              </template>
            </Column>
            <Column field="qta1560" header="Da 480 a 1560">
              <template #body="slotProps">
                <div :class="{ 'total-row': slotProps.data.isTotalRow }">
                  {{ formatNumber(slotProps.data.qta1560) }}
                </div>
              </template>
            </Column>
            <Column field="qtao1560" header="Oltre 1560">
              <template #body="slotProps">
                <div :class="{ 'total-row': slotProps.data.isTotalRow }">
                  {{ formatNumber(slotProps.data.qtao1560) }}
                </div>
              </template>
            </Column>
            <Column field="n120" header="Punti 0-120">
              <template #body="slotProps">
                <div :class="{ 'total-row': slotProps.data.isTotalRow }">
                  {{ formatNumber(slotProps.data.n120) }}
                </div>
              </template>
            </Column>
            <Column field="n480" header="Punti 120-480">
              <template #body="slotProps">
                <div :class="{ 'total-row': slotProps.data.isTotalRow }">
                  {{ formatNumber(slotProps.data.n480) }}
                </div>
              </template>
            </Column>
            <Column field="n1560" header="Punti 480-1560">
              <template #body="slotProps">
                <div :class="{ 'total-row': slotProps.data.isTotalRow }">
                  {{ formatNumber(slotProps.data.n1560) }}
                </div>
              </template>
            </Column>
            <Column field="no1560" header="Punti > 1560">
              <template #body="slotProps">
                <div :class="{ 'total-row': slotProps.data.isTotalRow }">
                  {{ formatNumber(slotProps.data.no1560) }}
                </div>
              </template>
            </Column>
            <Column field="distributore" header="Distributore">
              <template #body="slotProps">
                {{ slotProps.data.distributore }}
              </template>
            </Column>
            <Column field="distributorePiva" header="P.IVA Distributore">
              <template #body="slotProps">
                {{ slotProps.data.distributorePiva }}
              </template>
            </Column>
          </DataTable>

          <h3 class="mt-4">Domestici Centro-Nord</h3>
          <DataTable
            :value="[
              ...Object.values(dichiarazioneGasData.domesticiCentroNord || {}),
              {
                comune: 'TOTALE',
                catastale: '-',
                provincia: '-',
                qta120: Object.values(
                  dichiarazioneGasData.domesticiCentroNord || {}
                ).reduce(
                  (acc, curr) => acc + (parseFloat(curr.qta120) || 0),
                  0
                ),
                qta480: Object.values(
                  dichiarazioneGasData.domesticiCentroNord || {}
                ).reduce(
                  (acc, curr) => acc + (parseFloat(curr.qta480) || 0),
                  0
                ),
                qta1560: Object.values(
                  dichiarazioneGasData.domesticiCentroNord || {}
                ).reduce(
                  (acc, curr) => acc + (parseFloat(curr.qta1560) || 0),
                  0
                ),
                qtao1560: Object.values(
                  dichiarazioneGasData.domesticiCentroNord || {}
                ).reduce(
                  (acc, curr) => acc + (parseFloat(curr.qtao1560) || 0),
                  0
                ),
                n120: Object.values(
                  dichiarazioneGasData.domesticiCentroNord || {}
                ).reduce((acc, curr) => acc + (parseInt(curr.n120) || 0), 0),
                n480: Object.values(
                  dichiarazioneGasData.domesticiCentroNord || {}
                ).reduce((acc, curr) => acc + (parseInt(curr.n480) || 0), 0),
                n1560: Object.values(
                  dichiarazioneGasData.domesticiCentroNord || {}
                ).reduce((acc, curr) => acc + (parseInt(curr.n1560) || 0), 0),
                no1560: Object.values(
                  dichiarazioneGasData.domesticiCentroNord || {}
                ).reduce((acc, curr) => acc + (parseInt(curr.no1560) || 0), 0),
                distributore: '-',
                distributorePiva: '-',
                isTotalRow: false,
              },
            ]"
            scrollable
            scrollHeight="400px"
            showGridlines
          >
            <Column field="comune" header="Comune">
              <template #body="slotProps">
                <div :class="{ 'total-row': slotProps.data.isTotalRow }">
                  {{ slotProps.data.comune }}
                </div>
              </template>
            </Column>
            <Column field="catastale" header="Codice Catastale">
              <template #body="slotProps">
                {{ slotProps.data.catastale }}
              </template>
            </Column>
            <Column field="provincia" header="Provincia">
              <template #body="slotProps">
                {{ slotProps.data.provincia }}
              </template>
            </Column>
            <Column field="qta120" header="Fino a 120">
              <template #body="slotProps">
                <div :class="{ 'total-row': slotProps.data.isTotalRow }">
                  {{ formatNumber(slotProps.data.qta120) }}
                </div>
              </template>
            </Column>
            <Column field="qta480" header="Da 120 a 480">
              <template #body="slotProps">
                <div :class="{ 'total-row': slotProps.data.isTotalRow }">
                  {{ formatNumber(slotProps.data.qta480) }}
                </div>
              </template>
            </Column>
            <Column field="qta1560" header="Da 480 a 1560">
              <template #body="slotProps">
                <div :class="{ 'total-row': slotProps.data.isTotalRow }">
                  {{ formatNumber(slotProps.data.qta1560) }}
                </div>
              </template>
            </Column>
            <Column field="qtao1560" header="Oltre 1560">
              <template #body="slotProps">
                <div :class="{ 'total-row': slotProps.data.isTotalRow }">
                  {{ formatNumber(slotProps.data.qtao1560) }}
                </div>
              </template>
            </Column>
            <Column field="n120" header="Punti 0-120">
              <template #body="slotProps">
                <div :class="{ 'total-row': slotProps.data.isTotalRow }">
                  {{ formatNumber(slotProps.data.n120) }}
                </div>
              </template>
            </Column>
            <Column field="n480" header="Punti 120-480">
              <template #body="slotProps">
                <div :class="{ 'total-row': slotProps.data.isTotalRow }">
                  {{ formatNumber(slotProps.data.n480) }}
                </div>
              </template>
            </Column>
            <Column field="n1560" header="Punti 480-1560">
              <template #body="slotProps">
                <div :class="{ 'total-row': slotProps.data.isTotalRow }">
                  {{ formatNumber(slotProps.data.n1560) }}
                </div>
              </template>
            </Column>
            <Column field="no1560" header="Punti > 1560">
              <template #body="slotProps">
                <div :class="{ 'total-row': slotProps.data.isTotalRow }">
                  {{ formatNumber(slotProps.data.no1560) }}
                </div>
              </template>
            </Column>
            <Column field="distributore" header="Distributore">
              <template #body="slotProps">
                {{ slotProps.data.distributore }}
              </template>
            </Column>
            <Column field="distributorePiva" header="P.IVA Distributore">
              <template #body="slotProps">
                {{ slotProps.data.distributorePiva }}
              </template>
            </Column>
          </DataTable>

          <h3 class="mt-4">Altri Usi</h3>
          <DataTable
            :value="[
              ...Object.values(dichiarazioneGasData.altriUsi || {}),
              {
                comune: 'TOTALE',
                catastale: '-',
                provincia: '-',
                qta1200000: Object.values(
                  dichiarazioneGasData.altriUsi || {}
                ).reduce(
                  (acc, curr) => acc + (parseFloat(curr.qta1200000) || 0),
                  0
                ),
                qtao1200000: Object.values(
                  dichiarazioneGasData.altriUsi || {}
                ).reduce(
                  (acc, curr) => acc + (parseFloat(curr.qtao1200000) || 0),
                  0
                ),
                n1200000: Object.values(
                  dichiarazioneGasData.altriUsi || {}
                ).reduce(
                  (acc, curr) => acc + (parseInt(curr.n1200000) || 0),
                  0
                ),
                no1200000: Object.values(
                  dichiarazioneGasData.altriUsi || {}
                ).reduce(
                  (acc, curr) => acc + (parseInt(curr.no1200000) || 0),
                  0
                ),
                distributore: '-',
                distributorePiva: '-',
                isTotalRow: false,
              },
            ]"
            scrollable
            scrollHeight="400px"
            showGridlines
          >
            <Column field="comune" header="Comune">
              <template #body="slotProps">
                <div :class="{ 'total-row': slotProps.data.isTotalRow }">
                  {{ slotProps.data.comune }}
                </div>
              </template>
            </Column>
            <Column field="catastale" header="Codice Catastale">
              <template #body="slotProps">
                {{ slotProps.data.catastale }}
              </template>
            </Column>
            <Column field="provincia" header="Provincia">
              <template #body="slotProps">
                {{ slotProps.data.provincia }}
              </template>
            </Column>
            <Column field="qta1200000" header="Fino a 1.200.000">
              <template #body="slotProps">
                <div :class="{ 'total-row': slotProps.data.isTotalRow }">
                  {{ formatNumber(slotProps.data.qta1200000) }}
                </div>
              </template>
            </Column>
            <Column field="qtao1200000" header="Oltre 1.200.000">
              <template #body="slotProps">
                <div :class="{ 'total-row': slotProps.data.isTotalRow }">
                  {{ formatNumber(slotProps.data.qtao1200000) }}
                </div>
              </template>
            </Column>
            <Column field="n1200000" header="Punti 0-1.200.000">
              <template #body="slotProps">
                <div :class="{ 'total-row': slotProps.data.isTotalRow }">
                  {{ formatNumber(slotProps.data.n1200000) }}
                </div>
              </template>
            </Column>
            <Column field="no1200000" header="Punti > 1.200.000">
              <template #body="slotProps">
                <div :class="{ 'total-row': slotProps.data.isTotalRow }">
                  {{ formatNumber(slotProps.data.no1200000) }}
                </div>
              </template>
            </Column>
            <Column field="distributore" header="Distributore">
              <template #body="slotProps">
                {{ slotProps.data.distributore }}
              </template>
            </Column>
            <Column field="distributorePiva" header="P.IVA Distributore">
              <template #body="slotProps">
                {{ slotProps.data.distributorePiva }}
              </template>
            </Column>
          </DataTable>

          <h3 class="mt-4">Totale per Reseller</h3>
          <div class="totali-table p-grid">
            <div class="p-col-12">
              <div class="grid">
                <div class="col-6 font-bold">Ragione Sociale</div>
                <div class="col-6 font-bold">Totale Smc</div>
              </div>
              <div
                class="grid"
                v-for="reseller in dichiarazioneGasData.totaleReseller"
                :key="reseller.ragsoc"
              >
                <div class="col-6">{{ reseller.ragsoc }}</div>
                <div class="col-6">{{ formatNumber(reseller.totale) }}</div>
              </div>
            </div>
          </div>

          <h3 class="mt-4">Riepilogo Totali</h3>
          <DataTable
            :value="[dichiarazioneGasData.totali]"
            class="totali-table"
            showGridlines
          >
            <Column
              field="domesticiMezzogiorno"
              header="Totale Domestici Mezzogiorno"
            >
              <template #body="slotProps">
                {{ formatNumber(slotProps.data.domesticiMezzogiorno) }}
              </template>
            </Column>
            <Column
              field="domesticiCentroNord"
              header="Totale Domestici Centro-Nord"
            >
              <template #body="slotProps">
                {{ formatNumber(slotProps.data.domesticiCentroNord) }}
              </template>
            </Column>
            <Column field="altriUsi" header="Totale Altri Usi">
              <template #body="slotProps">
                {{ formatNumber(slotProps.data.altriUsi) }}
              </template>
            </Column>
            <Column field="complessivo" header="Totale Complessivo" class="colonna-complessivo">
            <template #body="slotProps">
              <div style="display: block !important; visibility: visible !important; min-width: 100px;">
          {{ formatNumber(slotProps.data.complessivo) }}
              </div>
            </template>
          </Column>
          </DataTable>
        </div>

        <!-- <div v-else class="no-data-message">
          Nessun dato disponibile per il periodo selezionato
        </div> -->
      </div>
    </section>

    <section v-if="showAddizionaliGas" class="inline-declaration-section">
      <h3 class="section-title">Addizionali GAS</h3>
      <div class="dichiarazione-gas-container declaration-content">
        <div v-if="dichiarazioneAddizionaliGasData" class="result-actions">
          <Button
            label="Esporta CSV"
            icon="pi pi-download"
            class="p-button-success"
            @click="exportAddizionaliGasToCsv"
          />
        </div>

        <div v-if="loadingAddizionaliGas" class="loading-indicator">
          <ProgressSpinner />
          <span>Caricamento dati in corso...</span>
        </div>

        <div v-else-if="dichiarazioneAddizionaliGasData">
          <h3>Riepilogo Periodo</h3>
          <DataTable
            :value="[
              {
                periodo: `${dichiarazioneAddizionaliGasData.periodo?.inizio || '-'} / ${
                  dichiarazioneAddizionaliGasData.periodo?.fine || '-'
                }`,
                totale_addizionale:
                  dichiarazioneAddizionaliGasData.riepilogo?.totale_addizionale || 0,
                totale_consumo:
                  dichiarazioneAddizionaliGasData.riepilogo?.totale_consumo || 0,
              },
            ]"
            showGridlines
          >
            <Column field="periodo" header="Periodo" />
            <Column field="totale_addizionale" header="Totale Addizionale">
              <template #body="slotProps">
                {{ formatCurrency(slotProps.data.totale_addizionale) }}
              </template>
            </Column>
            <Column field="totale_consumo" header="Totale Consumo kWh">
              <template #body="slotProps">
                {{ formatNumber(slotProps.data.totale_consumo) }}
              </template>
            </Column>
          </DataTable>

          <h3 class="mt-4">Totali per Regione</h3>
          <DataTable
            :value="addizionaliGasRows"
            scrollable
            scrollHeight="350px"
            showGridlines
          >
            <Column field="nome_regione" header="Regione / Provincia">
              <template #body="slotProps">
                <div
                  :class="{
                    'region-row': slotProps.data.rowType === 'regione',
                    'province-row': slotProps.data.rowType === 'provincia',
                  }"
                >
                  {{
                    slotProps.data.rowType === "regione"
                      ? slotProps.data.nome_regione
                      : `${slotProps.data.provincia} (${slotProps.data.sigla_provincia})`
                  }}
                </div>
              </template>
            </Column>
            <Column field="totale_addizionale_regione" header="Totale Addizionale">
              <template #body="slotProps">
                {{
                  formatCurrency(
                    slotProps.data.rowType === "regione"
                      ? slotProps.data.totale_addizionale_regione
                      : slotProps.data.totale_addizionale_regionale
                  )
                }}
              </template>
            </Column>
            <Column field="totale_consumo_regione" header="Totale Consumo kWh">
              <template #body="slotProps">
                {{
                  formatNumber(
                    slotProps.data.rowType === "regione"
                      ? slotProps.data.totale_consumo_regione
                      : slotProps.data.totale_consumo_kwh
                  )
                }}
              </template>
            </Column>
            <Column field="punti_totali_regione" header="Punti Totali">
              <template #body="slotProps">
                {{
                  formatNumber(
                    slotProps.data.rowType === "regione"
                      ? slotProps.data.punti_totali_regione
                      : slotProps.data.numero_punti_fornitura
                  )
                }}
              </template>
            </Column>
            <Column field="numero_fatture" header="Numero Fatture">
              <template #body="slotProps">
                {{ formatNumber(slotProps.data.numero_fatture || 0) }}
              </template>
            </Column>
          </DataTable>
        </div>

        <div v-else class="no-data" style="text-align: center; padding: 2rem">
          Seleziona i filtri e clicca Cerca
        </div>
      </div>
    </section>

    <section
      v-if="showDichiarazioneSemestraleEE"
      class="inline-declaration-section"
    >
      <h3 class="section-title">Consumi Semestrali EE</h3>
      <div class="dichiarazione-semestrale-dialog declaration-content declaration-form">
        <div class="field">
          <label for="dichiarazione-semestrale-ee-year">Anno</label>
          <Calendar
            id="dichiarazione-semestrale-ee-year"
            v-model="dichiarazioneSemestraleEEYear"
            view="year"
            dateFormat="yy"
            :showIcon="true"
            :manualInput="false"
            class="w-full"
          />
        </div>
        <div class="field semester-field">
          <span class="field-label">Semestre</span>
          <div class="semester-options">
            <div v-for="option in semestreOptions" :key="option.value" class="semester-option">
              <RadioButton
                v-model="dichiarazioneSemestraleEESemestre"
                :inputId="`dichiarazione-semestrale-ee-${option.value}`"
                name="dichiarazione-semestrale-ee-semestre"
                :value="option.value"
              />
              <label :for="`dichiarazione-semestrale-ee-${option.value}`">
                {{ option.label }}
              </label>
            </div>
          </div>
        </div>
        <div class="declaration-actions">
          <span class="declaration-action-label" aria-hidden="true">Azione</span>
          <Button
            label="Esporta XML"
            icon="pi pi-file"
            @click="downloadDichiarazioneSemestraleEEXml"
            class="p-button-info"
            :loading="isLoadingSemestraleEE"
          />
        </div>
      </div>
    </section>

    <section
      v-if="showDichiarazioneSemestraleGAS"
      class="inline-declaration-section"
    >
      <h3 class="section-title">Consumi Semestrali GAS</h3>
      <div class="dichiarazione-semestrale-dialog declaration-content declaration-form">
        <div class="field">
          <label for="dichiarazione-semestrale-gas-year">Anno</label>
          <Calendar
            id="dichiarazione-semestrale-gas-year"
            v-model="dichiarazioneSemestraleGASYear"
            view="year"
            dateFormat="yy"
            :showIcon="true"
            :manualInput="false"
            class="w-full"
          />
        </div>
        <div class="field semester-field">
          <span class="field-label">Semestre</span>
          <div class="semester-options">
            <div v-for="option in semestreOptions" :key="option.value" class="semester-option">
              <RadioButton
                v-model="dichiarazioneSemestraleGASSemestre"
                :inputId="`dichiarazione-semestrale-gas-${option.value}`"
                name="dichiarazione-semestrale-gas-semestre"
                :value="option.value"
              />
              <label :for="`dichiarazione-semestrale-gas-${option.value}`">
                {{ option.label }}
              </label>
            </div>
          </div>
        </div>
        <div class="declaration-actions">
          <span class="declaration-action-label" aria-hidden="true">Azione</span>
          <Button
            label="Esporta XML"
            icon="pi pi-file"
            @click="downloadDichiarazioneSemestraleGASXml"
            class="p-button-info"
            :loading="isLoadingSemestraleGAS"
          />
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { ref, onMounted, getCurrentInstance, watch, computed, nextTick } from "vue";
import Service from "@/services/ask-pratiche/dogane-service";
import Loader from "@/components/Loader.vue";
import * as XLSX from "xlsx";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Button from "primevue/button";
import Calendar from "primevue/calendar";
import Dropdown from "primevue/dropdown";
import ProgressSpinner from "primevue/progressspinner";
//import Toast from "primevue/toast";
//import { useToast } from "primevue/usetoast";

export default {
  components: {
    Loader,
    DataTable,
    Column,
    Button,
    Calendar,
    Dropdown,
    ProgressSpinner,
    // Toast,
  },
  setup() {
    const self = getCurrentInstance();
    self.$eventBus = self.appContext.config.globalProperties.$eventBus;
    self.$http = self.appContext.config.globalProperties.$http;
    const service = new Service(self.$http, self.$eventBus);
    const loader = ref(false);
    const selectedYear = ref(new Date().getFullYear());
    const commodityTabIndex = ref(null);
    const periodTabIndex = ref(null);
    const operationTabIndex = ref(null);
    const filterYear = ref(null);
    const filterSemester = ref(null);
    const filterMonth = ref(null);
    const currentYear = new Date().getFullYear();
    const currentYearMaxDate = new Date(currentYear, 11, 31);
    const filterConsumptionPeriod = ref(null);
    const filterAddizionaliPeriod = ref(null);
    const consumptionMaxDate = computed(() => {
      const [start, end] = filterConsumptionPeriod.value || [];
      return start instanceof Date && !end
        ? new Date(Math.min(new Date(start.getFullYear(), start.getMonth() + 12, 0).getTime(), currentYearMaxDate.getTime()))
        : currentYearMaxDate;
    });
    const hasValidConsumptionPeriod = computed(() => {
      const [start, end] = filterConsumptionPeriod.value || [];
      if (!(start instanceof Date) || !(end instanceof Date) || end < start) {
        return false;
      }

      const lastAllowedMonth = new Date(
        start.getFullYear(),
        start.getMonth() + 12,
        0
      );
      return end <= lastAllowedMonth && end <= currentYearMaxDate;
    });
    const addizionaliMaxDate = computed(() => {
      const [start, end] = filterAddizionaliPeriod.value || [];
      return start instanceof Date && !end
        ? new Date(Math.min(new Date(start.getFullYear(), start.getMonth() + 12, 0).getTime(), currentYearMaxDate.getTime()))
        : currentYearMaxDate;
    });
    const hasValidAddizionaliPeriod = computed(() => {
      const [start, end] = filterAddizionaliPeriod.value || [];
      if (!(start instanceof Date) || !(end instanceof Date) || end < start) {
        return false;
      }

      return end <= new Date(start.getFullYear(), start.getMonth() + 12, 0) && end <= currentYearMaxDate;
    });
    const selectedCommodity = computed(() =>
      commodityTabIndex.value === null ? null : commodityTabIndex.value === 0 ? "ee" : "gas"
    );
    const commodityOptions = [
      { label: "EE", index: 0 },
      { label: "GAS", index: 1 },
    ];
    const periodOptions = [
      { label: "Annuale", index: 0 },
      { label: "Mensile", index: 1 },
    ];
    const selectedPeriod = computed(
      () => ["annuale", "mensile"][periodTabIndex.value]
    );
    const availableOperations = computed(() => {
      const operations = [];
      if (!selectedCommodity.value || !selectedPeriod.value) return operations;

      if (selectedPeriod.value !== "semestrale") {
        operations.push({ label: "Accise", value: "accise" });
      }
      if (selectedPeriod.value !== "mensile") {
        operations.push({ label: "Consumi", value: "consumi" });
      }
      if (
        selectedCommodity.value === "gas" &&
        selectedPeriod.value === "annuale"
      ) {
        operations.push({ label: "Addizionali", value: "addizionali" });
      }

      return operations;
    });
    const selectedOperation = computed(
      () => availableOperations.value[operationTabIndex.value]?.value
    );
    const canSearch = computed(() => {
      if (!selectedCommodity.value || !selectedPeriod.value || !selectedOperation.value) return false;
      if (selectedOperation.value === "addizionali") return hasValidAddizionaliPeriod.value;
      if (selectedOperation.value === "consumi") return hasValidConsumptionPeriod.value;
      const date = selectedPeriod.value === "mensile" ? filterMonth.value : filterYear.value;
      return date instanceof Date && date.getFullYear() <= currentYear;
    });
    const operationOptions = computed(() =>
      availableOperations.value.map((operation, index) => ({
        ...operation,
        index,
      }))
    );
    const selectedYearsLabel = computed(() => {
      let dates = [];

      if (
        selectedPeriod.value === "annuale" &&
        selectedOperation.value === "consumi"
      ) {
        dates = filterConsumptionPeriod.value || [];
      } else if (selectedOperation.value === "addizionali") {
        dates = filterAddizionaliPeriod.value || [];
      } else if (selectedPeriod.value === "mensile") {
        dates = [filterMonth.value];
      } else {
        dates = [filterYear.value];
      }

      const years = dates
        .filter((date) => date instanceof Date && !Number.isNaN(date.getTime()))
        .map((date) => date.getFullYear())
        .filter((year, index, values) => values.indexOf(year) === index);

      return years.join("–");
    });
    const elettricoStatuti = ref([]);
    const gasStatuti = ref([]);
    const elettricoProvince = ref([]);
    const gasProvince = ref([]);

    const showElettricoData = ref(false);
    const showGasData = ref(false);

    const mensileMonthOptions = [
      { label: "Gennaio", value: "01" },
      { label: "Febbraio", value: "02" },
      { label: "Marzo", value: "03" },
      { label: "Aprile", value: "04" },
      { label: "Maggio", value: "05" },
      { label: "Giugno", value: "06" },
      { label: "Luglio", value: "07" },
      { label: "Agosto", value: "08" },
      { label: "Settembre", value: "09" },
      { label: "Ottobre", value: "10" },
      { label: "Novembre", value: "11" },
      { label: "Dicembre", value: "12" },
    ];

    const months = ref([...mensileMonthOptions]);

    const entrateUscite = ref(null);
    const entrateUsciteGas = ref(null);

    const loadElettricoData = async () => {
      loader.value = true;
      try {
        const response = await service.getStatutiElettrico({
          anno: selectedYear.value,
        });

        entrateUscite.value = response.entrateUscite;

        const statutiProvincia = transformData(response.provincia, true);
        const statutiRegione = transformData(response.regione, true);
        const ordinario = transformData(
          { ordinario: response.ordinario },
          true
        );

        elettricoStatuti.value = [
          ...statutiProvincia,
          ...statutiRegione,
          ...ordinario,
        ];

        elettricoProvince.value = transformData(response.provincia, false);
      } catch (error) {
        console.error("Errore nel caricamento dati elettrico:", error);
      } finally {
        loader.value = false;
      }
    };

    const loadGasData = async () => {
      loader.value = true;
      try {
        const response = await service.getStatutiGas({
          anno: selectedYear.value,
        });

        entrateUsciteGas.value = response.entrateUscite;

        const statutiProvincia = transformData(response.provincia, true);
        const statutiRegione = transformData(response.regione, true);
        const ordinario = transformData(
          { ordinario: response.ordinario },
          true
        );

        gasStatuti.value = [
          ...statutiProvincia,
          ...statutiRegione,
          ...ordinario,
        ];

        gasProvince.value = transformData(response.provincia, false);
      } catch (error) {
        console.error("Errore nel caricamento dati gas:", error);
      } finally {
        loader.value = false;
      }
    };

    const loadAllData = async () => {
      await Promise.all([loadElettricoData(), loadGasData()]);
    };

    const showElettrico = () => {
      showElettricoData.value = true;
      showGasData.value = false;
      loadElettricoData();
    };

    const showGas = () => {
      showGasData.value = true;
      showElettricoData.value = false;
      loadGasData();
    };

    const formatCurrency = (value) => {
      if (value === null || value === undefined) return "-";
      return new Intl.NumberFormat("it-IT", {
        style: "currency",
        currency: "EUR",
      }).format(value);
    };

    const transformData = (data, isStatuto) => {
      const result = [];
      const statutiList = [
        "Trento",
        "Bolzano",
        "Sicilia",
        "Sardegna",
        "Valle d'Aosta",
        "Friuli Venezia Giulia",
        "Ordinario",
      ];

      for (const key in data) {
        const item = data[key];
        const mesi = item.mesi || {};

        const transformedItem = {
          statuto: isStatuto ? item.statuto : key.toUpperCase(),
          riferimento: isStatuto ? item.riferimento : item.statuto,
          mesi: { ...mesi },
          conguaglio: item.conguaglio,
        };

        if (isStatuto && statutiList.includes(item.statuto)) {
          result.push(transformedItem);
        } else if (!isStatuto && !statutiList.includes(item.statuto)) {
          result.push(transformedItem);
        }
      }

      return result;
    };

    const downloadCsv = async (data, month) => {
      try {
        loader.value = true;

        if (!data.mesi || !data.mesi[month]) {
          console.error("Nessun dato disponibile per il mese selezionato");
          return;
        }

        const meseDati = data.mesi[month];

        const params = {
          mese: month,
          anno: selectedYear.value,
          meseAttuale: `${selectedYear.value}-${month}-01`,
          riferimento: data.riferimento,
          statuto: data.statuto,
          pods: meseDati.pods || [],
          rata: meseDati.rata,
          rataReale: meseDati.rataReale,
        };

        if (data.riferimento === "provincia") {
          params.code = data.statuto;
        } else if (data.riferimento === "regione") {
          params.regione = data.statuto.toLowerCase();
        } else if (data.riferimento === "ordinario") {
          params.ordinario = 1;
        }

        const response = await service.exportFornitureElettriche(params);

        if (response.type === "application/json") {
          const reader = new FileReader();
          reader.onload = () => {
            const error = JSON.parse(reader.result);
            console.error("Errore dal server:", error);
          };
          reader.readAsText(response);
          return;
        }

        const url = window.URL.createObjectURL(new Blob([response]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `forniture_${data.statuto}_${month}_${selectedYear.value}.csv`
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error("Errore durante il download del CSV:", error);
      } finally {
        loader.value = false;
      }
    };

    const showMensileEE = ref(false);
    const loading = ref(false);
    const mensileEEData = ref(null);
    const fasceArray = ref([]);
    const totali = ref({ totPod: 0, totConsumo: 0, totFatture: 0, totAccise: 0 });

    const getDefaultMonthValue = () =>
      String(new Date().getMonth() + 1).padStart(2, "0");

    const normalizeMonthValue = (value) => {
      const parsed = parseInt(String(value ?? ""), 10);
      if (!Number.isNaN(parsed) && parsed >= 1 && parsed <= 12) {
        return String(parsed).padStart(2, "0");
      }
      return getDefaultMonthValue();
    };

    const selectedMonth = ref(getDefaultMonthValue());
    const selectedAmbitoEE = ref("tutti");
    const ambitoOptions = [
      { label: "Tutti", value: "tutti" },
      { label: "Ordinario", value: "ordinario" },
      { label: "Bolzano", value: "bolzano" },
      { label: "Friuli", value: "friuli venezia giulia" },
      { label: "Sardegna", value: "sardegna" },
      { label: "Sicilia", value: "sicilia" },
      { label: "Trento", value: "trento" },
      { label: "Valle d'Aosta", value: "valle d'aosta" },
    ];

    const showMensileEETable = () => {
      showMensileEE.value = true;
      selectedMonth.value = getDefaultMonthValue();
      selectedYear.value = new Date().getFullYear();
      selectedAmbitoEE.value = "tutti";
      loadMensileEEData();
    };

    const loadMensileEEData = async () => {
      if (!selectedMonth.value || !selectedYear.value) {
        return;
      }

      loading.value = true;
      fasceArray.value = [];

      try {
        await new Promise((resolve) => setTimeout(resolve, 100));

        const params = {
          anno: selectedYear.value,
          mese: selectedMonth.value.toString().padStart(2, "0"),
        };
        if (selectedAmbitoEE.value && selectedAmbitoEE.value !== "tutti") {
          params.ambito = selectedAmbitoEE.value;
        }

        const response = await service.getDoganeMensiliEE(params);

        mensileEEData.value = response;

        if (response.fasce) {
          fasceArray.value = Object.entries(response.fasce).map(
            ([key, value]) => ({
              tipo: key,
              ...value,
            })
          );
        }

        totali.value = response.totali || {
          totPod: 0,
          totConsumo: 0,
          totFatture: 0,
          totAccise: 0,
        };
      } catch (error) {
        console.error("Errore nel caricamento dati mensili EE:", error);
        fasceArray.value = [];
      } finally {
        loading.value = false;
      }
    };

    const calculateQuotaPod = (pod) => {
      if (!totali.value.totPod) return 0;
      return ((pod / totali.value.totPod) * 100).toFixed(2);
    };

    const calculateQuotaConsumo = (consumo) => {
      if (!totali.value.totConsumo) return 0;
      return ((consumo / totali.value.totConsumo) * 100).toFixed(2);
    };

    const formatNumber = (number) => {
      if (number === null || number === undefined) return "-";
      return new Intl.NumberFormat("it-IT").format(number);
    };

    const showMensileGAS = ref(false);
    const loadingGAS = ref(false);
    const mensileGASData = ref(null);
    const fasceArrayGAS = ref([]);
    const totaliGAS = ref({ totPdr: 0, totConsumo: 0, totFatture: 0, totAccise: 0 });
    const selectedMonthGAS = ref(getDefaultMonthValue());
    const selectedYearGAS = ref(new Date().getFullYear());
    const selectedAmbitoGAS = ref("tutti");
    const mensileGasMonthKey = ref(0);

    const showMensileGASTable = () => {
      selectedMonthGAS.value = getDefaultMonthValue();
      selectedYearGAS.value = new Date().getFullYear();
      selectedAmbitoGAS.value = "tutti";
      showMensileGAS.value = true;
      nextTick(() => {
        selectedMonthGAS.value = normalizeMonthValue(selectedMonthGAS.value);
        mensileGasMonthKey.value += 1;
        loadMensileGASData();
      });
    };

    const loadMensileGASData = async () => {
      if (!selectedMonthGAS.value || !selectedYearGAS.value) {
        return;
      }

      try {
        loadingGAS.value = true;

        const params = {
          anno: selectedYearGAS.value,
          mese: selectedMonthGAS.value.toString().padStart(2, "0"),
        };
        if (selectedAmbitoGAS.value && selectedAmbitoGAS.value !== "tutti") {
          params.ambito = selectedAmbitoGAS.value;
        }

        const response = await service.getDoganeMensiliGas(params);

        mensileGASData.value = response;

        fasceArrayGAS.value = Object.entries(response.fasce).map(
          ([key, value]) => ({
            tipo: key,
            ...value,
          })
        );

        totaliGAS.value = response.totali || {
          totPdr: 0,
          totConsumo: 0,
          totFatture: 0,
          totAccise: 0,
        };
      } catch (error) {
        console.error("Errore nel caricamento dati mensili GAS:", error);
      } finally {
        loadingGAS.value = false;
      }
    };

    const calculateQuotaPdrGAS = (pdr) => {
      if (!totaliGAS.value.totPdr) return 0;
      return ((pdr / totaliGAS.value.totPdr) * 100).toFixed(2);
    };

    const calculateQuotaConsumoGAS = (consumo) => {
      if (!totaliGAS.value.totConsumo) return 0;
      return ((consumo / totaliGAS.value.totConsumo) * 100).toFixed(2);
    };

    const loadingDownload = ref(null);

    const downloadGasCsv = async (data, month) => {
      try {
        loader.value = true;

        if (!data.mesi || !data.mesi[month]) {
          console.error("Nessun dato disponibile per il mese selezionato");
          return;
        }

        const params = {
          mese: month,
          anno: selectedYear.value,
          meseAttuale: `${selectedYear.value}-${month}-01`,
          statuto: data.statuto,
          riferimento: data.riferimento,
        };

        const statutiSpeciali = [
          "Ordinario",
          "Bolzano",
          "Trento",
          "Sicilia",
          "Sardegna",
          "Valle d'Aosta",
          "Friuli Venezia Giulia",
        ];

        if (statutiSpeciali.includes(data.statuto)) {
          params.statuto = data.statuto;
          if (data.statuto === "Ordinario") {
            params.riferimento = "Bari";
          }
        } else {
          params.code = data.statuto;
        }

        const response = await service.exportFornitureGas(params);

        if (response.type === "application/json") {
          const reader = new FileReader();
          reader.onload = () => {
            const error = JSON.parse(reader.result);
            console.error("Errore dal server:", error);
          };
          reader.readAsText(response);
          return;
        }

        const url = window.URL.createObjectURL(new Blob([response]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `forniture_gas_${data.statuto}_${month}_${selectedYear.value}.csv`
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error("Errore durante il download del CSV:", error);
      } finally {
        loader.value = false;
      }
    };

    const getPreviousMonthValue = (mesi, currentMonth) => {
      const currentMonthNum = parseInt(currentMonth);
      const previousMonth = (currentMonthNum - 1).toString().padStart(2, "0");

      if (currentMonthNum === 1) return 0;

      return mesi[previousMonth]?.pods || 0;
    };

    const getPreviousMonthValueGas = (mesi, currentMonth) => {
      const currentMonthNum = parseInt(currentMonth);
      const previousMonth = (currentMonthNum - 1).toString().padStart(2, "0");

      if (currentMonthNum === 1) return 0;

      return mesi[previousMonth]?.pdrs || 0;
    };

    const calculateMonthlyTotal = (data, month, field) => {
      if (!data || !data.length) return 0;

      return data.reduce((total, row) => {
        const monthData = row.mesi[month.padStart(2, "0")];
        if (monthData && monthData[field]) {
          return total + monthData[field];
        }
        return total;
      }, 0);
    };

    const exportToExcel = () => {
      if (showElettricoData.value) {
        const statutiData = [
          ...elettricoStatuti.value.filter((item) => item.statuto !== "Totali"),
          {
            statuto: "Totali",
            mesi: Object.fromEntries(
              months.value.map((month) => [
                month.value,
                {
                  pods: calculateMonthlyTotal(
                    elettricoStatuti.value,
                    month.value,
                    "pods"
                  ),
                  rataReale: calculateMonthlyTotal(
                    elettricoStatuti.value,
                    month.value,
                    "rataReale"
                  ),
                },
              ])
            ),
            conguaglio: { acciseFatturate: getTotalConguaglio(false) },
          },
          {
            statuto: "Totale Progressivo",
            mesi: Object.fromEntries(
              months.value.map((month) => [
                month.value,
                {
                  rataReale: calculateProgressiveTotal(
                    months.value,
                    month.value,
                    elettricoStatuti.value
                  ),
                },
              ])
            ),
            conguaglio: { acciseFatturate: getTotalConguaglio(false) },
          },
        ];

        const provinceData = [
          ...elettricoProvince.value.filter(
            (item) => item.statuto !== "Totali"
          ),
          {
            statuto: "Totali",
            mesi: Object.fromEntries(
              months.value.map((month) => [
                month.value,
                {
                  pods: calculateMonthlyTotal(
                    elettricoProvince.value,
                    month.value,
                    "pods"
                  ),
                  rataReale: calculateMonthlyTotal(
                    elettricoProvince.value,
                    month.value,
                    "rataReale"
                  ),
                },
              ])
            ),
            conguaglio: { acciseFatturate: getTotalConguaglio(true) },
          },
          {
            statuto: "Totale Progressivo",
            mesi: Object.fromEntries(
              months.value.map((month) => [
                month.value,
                {
                  rataReale: calculateProgressiveTotal(
                    months.value,
                    month.value,
                    elettricoProvince.value
                  ),
                },
              ])
            ),
            conguaglio: { acciseFatturate: getTotalConguaglio(true) },
          },
        ];

        const fileName = "dogane_elettrico.xlsx";

        const workbook = XLSX.utils.book_new();

        const prepareData = (data) => {
          const exportData = [
            {
              [showElettricoData.value ? "Statuto" : "Provincia"]: "",
            },
          ];

          months.value.forEach((month) => {
            exportData[0][month.label] = "";
          });
          exportData[0]["Conguaglio"] = "";

          data.forEach((row) => {
            const dataRow = {
              [showElettricoData.value ? "Statuto" : "Provincia"]: row.statuto,
            };

            months.value.forEach((month) => {
              const monthData = row.mesi[month.value] || {};
              const accise = monthData.rataReale || 0;

              if (row.statuto === "Totale Progressivo") {
                dataRow[month.label] = `Accise: ${formatCurrency(accise)}`;
              } else {
                const podsPdrs =
                  monthData[showElettricoData.value ? "pods" : "pdrs"] || 0;
                const prefix = row.statuto === "Totali" ? "TOT " : "";
                dataRow[month.label] = `${prefix}${
                  showElettricoData.value ? "POD" : "PDR"
                }: ${podsPdrs}\n${prefix}Accise: ${formatCurrency(accise)}`;
              }
            });

            if (row.conguaglio && row.statuto !== "Totale Progressivo") {
              dataRow["Conguaglio"] = `Accise Fatturate: ${formatCurrency(
                row.conguaglio.acciseFatturate || 0
              )}`;
            }

            exportData.push(dataRow);
          });

          return exportData;
        };

        const statutiSheet = XLSX.utils.json_to_sheet(
          prepareData(statutiData, "Statuti")
        );
        XLSX.utils.book_append_sheet(workbook, statutiSheet, "Statuti");

        const provinceSheet = XLSX.utils.json_to_sheet(
          prepareData(provinceData, "Province")
        );
        XLSX.utils.book_append_sheet(workbook, provinceSheet, "Province");

        ["Statuti", "Province"].forEach((sheetName) => {
          const sheet = workbook.Sheets[sheetName];
          const range = XLSX.utils.decode_range(sheet["!ref"]);

          const cols = [];
          for (let C = 0; C <= range.e.c; C++) {
            cols.push({ wch: C === 0 ? 20 : 18 });
          }
          sheet["!cols"] = cols;

          const rows = [];
          for (let R = 0; R <= range.e.r; R++) {
            rows.push({ hpt: 45 });
          }
          sheet["!rows"] = rows;

          for (let R = 1; R <= range.e.r; R++) {
            for (let C = 0; C <= range.e.c; C++) {
              const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
              if (sheet[cellAddress]) {
                sheet[cellAddress].s = {
                  alignment: {
                    vertical: "center",
                    horizontal: "left",
                    wrapText: true,
                  },
                  font: {
                    sz: 10,
                  },
                };
              }
            }
          }

          for (let C = 0; C <= range.e.c; C++) {
            const address = XLSX.utils.encode_cell({ r: 0, c: C });
            if (sheet[address]) {
              sheet[address].s = {
                font: {
                  bold: true,
                  color: { rgb: "FFFFFF" },
                },
                fill: { fgColor: { rgb: "4F81BD" } },
              };
            }
          }

          const lastRow = range.e.r;
          for (let C = 0; C <= range.e.c; C++) {
            const address = XLSX.utils.encode_cell({ r: lastRow, c: C });
            if (sheet[address]) {
              sheet[address].s = {
                font: { bold: true },
                fill: { fgColor: { rgb: "F2F2F2" } },
              };
            }
          }
        });

        XLSX.writeFile(workbook, fileName);
      } else if (showGasData.value) {
        const statutiData = [
          ...gasStatuti.value.filter((item) => item.statuto !== "Totali"),
          {
            statuto: "Totali",
            mesi: Object.fromEntries(
              months.value.map((month) => [
                month.value,
                {
                  pdrs: calculateMonthlyTotal(
                    gasStatuti.value,
                    month.value,
                    "pdrs"
                  ),
                  rataReale: calculateMonthlyTotal(
                    gasStatuti.value,
                    month.value,
                    "rataReale"
                  ),
                },
              ])
            ),
            conguaglio: { acciseFatturate: getTotalConguaglio(false) },
          },
          {
            statuto: "Totale Progressivo",
            mesi: Object.fromEntries(
              months.value.map((month) => [
                month.value,
                {
                  rataReale: calculateProgressiveTotal(
                    months.value,
                    month.value,
                    gasStatuti.value
                  ),
                },
              ])
            ),
            conguaglio: { acciseFatturate: getTotalConguaglio(false) },
          },
        ];

        const provinceData = [
          ...gasProvince.value.filter((item) => item.statuto !== "Totali"),
          {
            statuto: "Totali",
            mesi: Object.fromEntries(
              months.value.map((month) => [
                month.value,
                {
                  pdrs: calculateMonthlyTotal(
                    gasProvince.value,
                    month.value,
                    "pdrs"
                  ),
                  rataReale: calculateMonthlyTotal(
                    gasProvince.value,
                    month.value,
                    "rataReale"
                  ),
                },
              ])
            ),
            conguaglio: { acciseFatturate: getTotalConguaglio(true) },
          },
          {
            statuto: "Totale Progressivo",
            mesi: Object.fromEntries(
              months.value.map((month) => [
                month.value,
                {
                  rataReale: calculateProgressiveTotal(
                    months.value,
                    month.value,
                    gasProvince.value
                  ),
                },
              ])
            ),
            conguaglio: { acciseFatturate: getTotalConguaglio(true) },
          },
        ];

        const fileName = "dogane_gas.xlsx";

        const workbook = XLSX.utils.book_new();

        const prepareData = (data) => {
          const exportData = [
            {
              [showElettricoData.value ? "Statuto" : "Provincia"]: "",
            },
          ];

          months.value.forEach((month) => {
            exportData[0][month.label] = "";
          });
          exportData[0]["Conguaglio"] = "";

          data.forEach((row) => {
            const dataRow = {
              [showElettricoData.value ? "Statuto" : "Provincia"]: row.statuto,
            };

            months.value.forEach((month) => {
              const monthData = row.mesi[month.value] || {};
              const accise = monthData.rataReale || 0;

              if (row.statuto === "Totale Progressivo") {
                dataRow[month.label] = `Accise: ${formatCurrency(accise)}`;
              } else {
                const podsPdrs =
                  monthData[showElettricoData.value ? "pods" : "pdrs"] || 0;
                const prefix = row.statuto === "Totali" ? "TOT " : "";
                dataRow[month.label] = `${prefix}${
                  showElettricoData.value ? "POD" : "PDR"
                }: ${podsPdrs}\n${prefix}Accise: ${formatCurrency(accise)}`;
              }
            });

            if (row.conguaglio && row.statuto !== "Totale Progressivo") {
              dataRow["Conguaglio"] = `Accise Fatturate: ${formatCurrency(
                row.conguaglio.acciseFatturate || 0
              )}`;
            }

            exportData.push(dataRow);
          });

          return exportData;
        };

        const statutiSheet = XLSX.utils.json_to_sheet(
          prepareData(statutiData, "Statuti")
        );
        XLSX.utils.book_append_sheet(workbook, statutiSheet, "Statuti");

        const provinceSheet = XLSX.utils.json_to_sheet(
          prepareData(provinceData, "Province")
        );
        XLSX.utils.book_append_sheet(workbook, provinceSheet, "Province");

        ["Statuti", "Province"].forEach((sheetName) => {
          const sheet = workbook.Sheets[sheetName];
          const range = XLSX.utils.decode_range(sheet["!ref"]);

          const cols = [];
          for (let C = 0; C <= range.e.c; C++) {
            cols.push({ wch: C === 0 ? 20 : 18 });
          }
          sheet["!cols"] = cols;

          const rows = [];
          for (let R = 0; R <= range.e.r; R++) {
            rows.push({ hpt: 45 });
          }
          sheet["!rows"] = rows;

          for (let R = 1; R <= range.e.r; R++) {
            for (let C = 0; C <= range.e.c; C++) {
              const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
              if (sheet[cellAddress]) {
                sheet[cellAddress].s = {
                  alignment: {
                    vertical: "center",
                    horizontal: "left",
                    wrapText: true,
                  },
                  font: {
                    sz: 10,
                  },
                };
              }
            }
          }

          for (let C = 0; C <= range.e.c; C++) {
            const address = XLSX.utils.encode_cell({ r: 0, c: C });
            if (sheet[address]) {
              sheet[address].s = {
                font: {
                  bold: true,
                  color: { rgb: "FFFFFF" },
                },
                fill: { fgColor: { rgb: "4F81BD" } },
              };
            }
          }

          const lastRow = range.e.r;
          for (let C = 0; C <= range.e.c; C++) {
            const address = XLSX.utils.encode_cell({ r: lastRow, c: C });
            if (sheet[address]) {
              sheet[address].s = {
                font: { bold: true },
                fill: { fgColor: { rgb: "F2F2F2" } },
              };
            }
          }
        });

        XLSX.writeFile(workbook, fileName);
      }
    };

    const showDichiarazioneEE = ref(false);
    const createDefaultAnnualPeriod = () => {
      const year = new Date().getFullYear() - 1;
      return [new Date(year, 0, 1), new Date(year, 11, 1)];
    };
    const getAnnualPeriodParams = (period) => {
      if (
        !Array.isArray(period) ||
        period.length !== 2 ||
        !(period[0] instanceof Date) ||
        Number.isNaN(period[0].getTime()) ||
        !(period[1] instanceof Date) ||
        Number.isNaN(period[1].getTime())
      ) {
        return null;
      }

      return {
        anno_da: period[0].getFullYear(),
        mese_da: String(period[0].getMonth() + 1).padStart(2, "0"),
        anno_a: period[1].getFullYear(),
        mese_a: String(period[1].getMonth() + 1).padStart(2, "0"),
      };
    };
    const formatAnnualPeriod = (params) =>
      `${params.anno_da}${params.mese_da}-${params.anno_a}${params.mese_a}`;
    const dichiarazionePeriod = ref(createDefaultAnnualPeriod());
    const hasValidDichiarazionePeriod = computed(
      () => getAnnualPeriodParams(dichiarazionePeriod.value) !== null
    );
    const dichiarazioneData = ref(null);
    const dichiarazioneLoading = ref(false);

    const loadDichiarazioneData = async () => {
      const params = getAnnualPeriodParams(dichiarazionePeriod.value);
      if (!params) return;

      dichiarazioneLoading.value = true;
      try {
        const response = await service.getDichiarazioneAnnualeEE(params);

        dichiarazioneData.value = {
          domesticiResidenti: response.domesticiResidenti || [],
          domesticiNonResidenti: response.domesticiNonResidenti || [],
          altriUsi: response.altriUsi || [],
          totaleReseller: response.totaleReseller || [],
          hasRiepilogo: response.riepilogo != null,
          riepilogo: response.riepilogo || {
            totaleDomesticiResidenti: 0,
            totaleDomesticiNonResidenti: 0,
            totaleAltriUsi: 0,
            totaleReseller: 0,
            totaleComplessivo: 0,
          },
        };
      } catch (error) {
        console.error("Errore nel caricamento dati:", error);
      } finally {
        dichiarazioneLoading.value = false;
      }
    };

    watch(showDichiarazioneEE, (newValue) => {
      if (!newValue) {
        dichiarazioneData.value = null;
      }
    });

    const hasDichiarazioneData = computed(() => {
      return (
        dichiarazioneData.value &&
        (dichiarazioneData.value.hasRiepilogo ||
          dichiarazioneData.value.domesticiResidenti?.length > 0 ||
          dichiarazioneData.value.domesticiNonResidenti?.length > 0 ||
          dichiarazioneData.value.altriUsi?.length > 0 ||
          dichiarazioneData.value.totaleReseller?.length > 0)
      );
    });

    const downloadDichiarazioneCsv = () => {
      try {
        const params = getAnnualPeriodParams(dichiarazionePeriod.value);
        if (!params) return;

        const domesticiResidentiCsv = prepareCsvDataEE(
          dichiarazioneData.value.domesticiResidenti,
          [
            { header: "Comune", field: "comune" },
            { header: "Codice Catastale", field: "catastale" },
            { header: "Provincia", field: "provincia" },
            { header: "Fino a 150 kWh", field: "qta150" },
            { header: "Oltre 150 kWh", field: "qtao150" },
            { header: "Punti 0-150", field: "nmin150" },
            { header: "Punti > 150", field: "nmax150" },
            { header: "Distributore", field: "distributore" },
          ],
          "Domestici Residenti"
        );

        const domesticiNonResidentiCsv = prepareCsvDataEE(
          dichiarazioneData.value.domesticiNonResidenti,
          [
            { header: "Comune", field: "comune" },
            { header: "Codice Catastale", field: "catastale" },
            { header: "Provincia", field: "provincia" },
            { header: "Energia", field: "qta" },
            { header: "Punti", field: "npunti" },
            { header: "Distributore", field: "distributore" },
          ],
          "Domestici Non Residenti"
        );

        const altriUsiCsv = prepareCsvDataEE(
          dichiarazioneData.value.altriUsi,
          [
            { header: "Comune", field: "comune" },
            { header: "Codice Catastale", field: "catastale" },
            { header: "Provincia", field: "provincia" },
            { header: "Fino a 200k", field: "qta200" },
            { header: "Oltre 200k", field: "qtao200" },
            { header: "Punti 0-200k", field: "nmin200" },
            { header: "Punti > 200k", field: "nmax200" },
            { header: "Distributore", field: "distributore" },
          ],
          "Altri Usi"
        );

        const resellerCsv = prepareCsvDataEE(
          dichiarazioneData.value.totaleReseller,
          [
            { header: "Ragione Sociale", field: "ragsoc" },
            { header: "Totale kWh", field: "totale" },
          ],
          "Totale Reseller"
        );

        const riepilogoCsv = prepareCsvDataEE(
          [dichiarazioneData.value.riepilogo],
          [
            {
              header: "Totale Domestici Residenti",
              field: "totaleDomesticiResidenti",
            },
            {
              header: "Totale Domestici Non Residenti",
              field: "totaleDomesticiNonResidenti",
            },
            { header: "Totale Altri Usi", field: "totaleAltriUsi" },
            { header: "Totale Reseller", field: "totaleReseller" },
            { header: "Totale Complessivo", field: "totaleComplessivo" },
          ],
          "Riepilogo Totali"
        );

        const finalCsv = [
          domesticiResidentiCsv,
          "\n\n",
          domesticiNonResidentiCsv,
          "\n\n",
          altriUsiCsv,
          "\n\n",
          resellerCsv,
          "\n\n",
          riepilogoCsv,
        ].join("");

        const blob = new Blob(["\ufeff" + finalCsv], {
          type: "text/csv;charset=utf-8;",
        });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);

        const periodoEE = formatAnnualPeriod(params);

        link.setAttribute("href", url);
        link.setAttribute("download", `Dichiarazione_EE_${periodoEE}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error("Errore durante l'esportazione CSV:", error);
      }
    };

    const prepareCsvDataEE = (data, columns, title) => {
      if (!data || !data.length) return "";
      let csv = title + "\n";
      csv += columns.map((col) => `"${col.header}"`).join(";") + "\n";
      csv += data
        .map((row) => {
          return columns
            .map((col) => {
              const value = row[col.field];
              const formattedValue =
                typeof value === "number" ? formatNumber(value) : value || "";
              return `"${formattedValue}"`;
            })
            .join(";");
        })
        .join("\n");
      return csv;
    };

    const prepareCsvDataGas = (data, columns, title) => {
      if (!data || !data.length) return "";
      let csv = title + "\n";
      csv += columns.map((col) => `"${col.header}"`).join(";") + "\n";
      csv += data
        .map((row) => {
          return columns
            .map((col) => {
              const value = row[col.field];
              const formattedValue =
                typeof value === "number" ? formatNumber(value) : value || "";
              return `"${formattedValue}"`;
            })
            .join(";");
        })
        .join("\n");
      return csv;
    };

    const showDichiarazioneGAS = ref(false);
    const dichiarazioneAddizionaliGasData = ref(null);
    const showAddizionaliGas = ref(false);
    const loadingAddizionaliGas = ref(false);
    const addizionaliSearchMode = ref("anno");
    const addizionaliYear = ref(new Date().getFullYear() - 1);
    const addizionaliYearFrom = ref(new Date().getFullYear() - 1);
    const addizionaliYearTo = ref(new Date().getFullYear() - 1);
    const addizionaliMonthFrom = ref("01");
    const addizionaliMonthTo = ref("12");
    const dichiarazioneGasPeriod = ref(createDefaultAnnualPeriod());
    const hasValidDichiarazioneGasPeriod = computed(
      () => getAnnualPeriodParams(dichiarazioneGasPeriod.value) !== null
    );
    const dichiarazioneGasData = ref(null);
    const dichiarazioneGasLoading = ref(false);

    const loadDichiarazioneGasData = async () => {
      const params = getAnnualPeriodParams(dichiarazioneGasPeriod.value);
      if (!params) return;

      dichiarazioneGasLoading.value = true;
      try {
        const response = await service.getDichiarazioneAnnualeGas(params);
        dichiarazioneGasData.value = response;
      } catch (error) {
        console.error("Errore nel caricamento dati:", error);
      } finally {
        dichiarazioneGasLoading.value = false;
      }
    };

    const openAddizionaliGasDialog = () => {
      showAddizionaliGas.value = true;
      dichiarazioneAddizionaliGasData.value = null;
      addizionaliSearchMode.value = "anno";
      addizionaliYear.value = selectedYear.value || new Date().getFullYear() - 1;
      addizionaliYearFrom.value = addizionaliYear.value;
      addizionaliYearTo.value = addizionaliYear.value;
      addizionaliMonthFrom.value = "01";
      addizionaliMonthTo.value = "12";
    };

    const loadDichiarazioneAddizionaliGas = async () => {
      loadingAddizionaliGas.value = true;
      try {
        const params =
          addizionaliSearchMode.value === "periodo"
            ? {
                anno_da: addizionaliYearFrom.value,
                mese_da: addizionaliMonthFrom.value,
                anno_a: addizionaliYearTo.value,
                mese_a: addizionaliMonthTo.value,
              }
            : {
                anno: addizionaliYear.value,
              };

        const response = await service.getDichiarazioneAnnualeAddizionali(params);
        dichiarazioneAddizionaliGasData.value = response;
      } catch (error) {
        console.error(
          "Errore nel caricamento dati dichiarazione annuale addizionali GAS:",
          error
        );
      } finally {
        loadingAddizionaliGas.value = false;
      }
    };

    const exportAddizionaliGasToCsv = () => {
      if (!dichiarazioneAddizionaliGasData.value) return;

      const data = dichiarazioneAddizionaliGasData.value;
      const periodo = data.periodo || {};
      const riepilogo = data.riepilogo || {};
      const regioni = data.dati || [];

      const escapeCsv = (value) => {
        const safeValue = value === null || value === undefined ? "" : value;
        return `"${String(safeValue).replace(/"/g, '""')}"`;
      };
      const formatCsvNumeric = (value) => {
        const numericValue = Number(value) || 0;
        return new Intl.NumberFormat("it-IT", {
          maximumFractionDigits: 2,
        }).format(numericValue);
      };

      let csv = "";

      csv += "Riepilogo\n";
      csv +=
        "Periodo Inizio;Periodo Fine;Totale Addizionale;Totale Consumo kWh\n";
      csv += [
        escapeCsv(periodo.inizio || ""),
        escapeCsv(periodo.fine || ""),
        escapeCsv(formatCsvNumeric(riepilogo.totale_addizionale)),
        escapeCsv(formatCsvNumeric(riepilogo.totale_consumo)),
      ].join(";");
      csv += "\n\n";

      csv += "Totali per Regione\n";
      csv +=
        "Regione / Provincia;Totale Addizionale;Totale Consumo kWh;Punti Totali;Numero Fatture\n";
      csv += regioni
        .flatMap((regione) => {
          const province = regione.province || [];
          const totaleFattureRegione = province.reduce(
            (acc, provincia) => acc + (Number(provincia.numero_fatture) || 0),
            0
          );

          const regioneRow = [
            escapeCsv(regione.nome_regione || ""),
            escapeCsv(formatCsvNumeric(regione.totale_addizionale_regione)),
            escapeCsv(formatCsvNumeric(regione.totale_consumo_regione)),
            escapeCsv(formatCsvNumeric(regione.punti_totali_regione)),
            escapeCsv(formatCsvNumeric(totaleFattureRegione)),
          ].join(";");

          const provinceRows = province.map((provincia) =>
            [
              escapeCsv(
                `  - ${provincia.provincia || ""} (${provincia.sigla_provincia || ""})`
              ),
              escapeCsv(formatCsvNumeric(provincia.totale_addizionale_regionale)),
              escapeCsv(formatCsvNumeric(provincia.totale_consumo_kwh)),
              escapeCsv(formatCsvNumeric(provincia.numero_punti_fornitura)),
              escapeCsv(formatCsvNumeric(provincia.numero_fatture)),
            ].join(";")
          );

          return [regioneRow, ...provinceRows];
        })
        .join("\n");

      const annoFile = selectedYear.value || new Date().getFullYear();
      const blob = new Blob(["\ufeff" + csv], {
        type: "text/csv;charset=utf-8;",
      });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", `addizionali_gas_${annoFile}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    };

    const addizionaliGasRows = computed(() => {
      const regioni = dichiarazioneAddizionaliGasData.value?.dati || [];
      if (!regioni.length) return [];

      return regioni.flatMap((regione) => {
        const province = regione.province || [];
        const totalFatture = province.reduce(
          (acc, provincia) => acc + (Number(provincia.numero_fatture) || 0),
          0
        );

        const regioneRow = {
          ...regione,
          rowType: "regione",
          numero_fatture: totalFatture,
        };

        const provinceRows = province.map((provincia) => ({
          ...provincia,
          rowType: "provincia",
          nome_regione: regione.nome_regione,
        }));

        return [regioneRow, ...provinceRows];
      });
    });

    const downloadDichiarazioneGasCsv = () => {
      try {
        const params = getAnnualPeriodParams(dichiarazioneGasPeriod.value);
        if (!params) return;

        const domesticiMezzogiornoArray = Object.values(
          dichiarazioneGasData.value.domesticiMezzogiorno || {}
        );
        const domesticiCentroNordArray = Object.values(
          dichiarazioneGasData.value.domesticiCentroNord || {}
        );

        const domesticiMezzogiornoCSV = prepareCsvDataGas(
          domesticiMezzogiornoArray,
          [
            { header: "Comune", field: "comune" },
            { header: "Codice Catastale", field: "catastale" },
            { header: "Provincia", field: "provincia" },
            { header: "Fino a 120 mc", field: "qta120" },
            { header: "Da 120 a 480 mc", field: "qta480" },
            { header: "Da 480 a 1560 mc", field: "qta1560" },
            { header: "Oltre 1560 mc", field: "qtao1560" },
            { header: "Punti 0-120", field: "n120" },
            { header: "Punti 120-480", field: "n480" },
            { header: "Punti 480-1560", field: "n1560" },
            { header: "Punti > 1560", field: "no1560" },
            { header: "Distributore", field: "distributore" },
          ],
          "Domestici Mezzogiorno"
        );

        const domesticiCentroNordCSV = prepareCsvDataGas(
          domesticiCentroNordArray,
          [
            { header: "Comune", field: "comune" },
            { header: "Codice Catastale", field: "catastale" },
            { header: "Provincia", field: "provincia" },
            { header: "Fino a 120 mc", field: "qta120" },
            { header: "Da 120 a 480 mc", field: "qta480" },
            { header: "Da 480 a 1560 mc", field: "qta1560" },
            { header: "Oltre 1560 mc", field: "qtao1560" },
            { header: "Punti 0-120", field: "n120" },
            { header: "Punti 120-480", field: "n480" },
            { header: "Punti 480-1560", field: "n1560" },
            { header: "Punti > 1560", field: "no1560" },
            { header: "Distributore", field: "distributore" },
          ],
          "Domestici Centro-Nord"
        );

        const altriUsiCSV = prepareCsvDataGas(
          dichiarazioneGasData.value.altriUsi || [],
          [
            { header: "Comune", field: "comune" },
            { header: "Codice Catastale", field: "catastale" },
            { header: "Provincia", field: "provincia" },
            { header: "Fino a 1.200.000 mc", field: "qta1200000" },
            { header: "Oltre 1.200.000 mc", field: "qtao1200000" },
            { header: "Punti 0-1.200.000", field: "n1200000" },
            { header: "Punti > 1.200.000", field: "no1200000" },
            { header: "Distributore", field: "distributore" },
          ],
          "Altri Usi"
        );

        const riepilogoCSV = prepareCsvDataGas(
          [dichiarazioneGasData.value.totali || {}],
          [
            {
              header: "Totale Domestici Mezzogiorno (mc)",
              field: "domesticiMezzogiorno",
            },
            {
              header: "Totale Domestici Centro-Nord (mc)",
              field: "domesticiCentroNord",
            },
            { header: "Totale Altri Usi (mc)", field: "altriUsi" },
            { header: "Totale Complessivo (mc)", field: "complessivo" },
          ],
          "Riepilogo Totali"
        );

        const periodoGas = formatAnnualPeriod(params);

        const finalCsv = [
          `Dichiarazione Annuale Gas - Periodo ${periodoGas}\n\n`,
          domesticiMezzogiornoCSV,
          "\n\n",
          domesticiCentroNordCSV,
          "\n\n",
          altriUsiCSV,
          "\n\n",
          riepilogoCSV,
        ].join("");

        const blob = new Blob(["\ufeff" + finalCsv], {
          type: "text/csv;charset=utf-8;",
        });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);

        link.setAttribute("href", url);
        link.setAttribute("download", `Dichiarazione_GAS_${periodoGas}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Errore durante l'esportazione CSV:", error);
      }
    };

    watch(showDichiarazioneGAS, (newValue) => {
      if (!newValue) {
        dichiarazioneGasData.value = null;
      }
    });

    onMounted(() => {
      //loadAllData();
    });

    const getTotalByProvincia = (provincia, field, tipo) => {
      if (!dichiarazioneData.value || !dichiarazioneData.value[tipo]) return 0;

      return dichiarazioneData.value[tipo]
        .filter((item) => item.provincia === provincia)
        .reduce((acc, curr) => acc + (curr[field] || 0), 0);
    };

    const downloadDichiarazioneXml = async () => {
      const params = getAnnualPeriodParams(dichiarazionePeriod.value);
      if (!params) return;

      try {
        isLoadingEE.value = true;
        const response = await service.getDichiarazioneXml("annuale", "ee", params);

        const url = window.URL.createObjectURL(
          new Blob([response], { type: "application/xml" })
        );
        const link = document.createElement("a");
        link.href = url;

        const periodoEE = formatAnnualPeriod(params);

        link.setAttribute("download", `Dichiarazione_EE_${periodoEE}.xml`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error("Errore durante il download del file XML:", error);
      } finally {
        isLoadingEE.value = false;
      }
    };

    const downloadDichiarazioneGasXml = async () => {
      const params = getAnnualPeriodParams(dichiarazioneGasPeriod.value);
      if (!params) return;

      try {
        isLoadingGas.value = true;
        const response = await service.getDichiarazioneXml("annuale", "gas", params);

        const url = window.URL.createObjectURL(
          new Blob([response], { type: "application/xml" })
        );
        const link = document.createElement("a");
        link.href = url;

        const periodoGas = formatAnnualPeriod(params);

        link.setAttribute("download", `Dichiarazione_GAS_${periodoGas}.xml`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error("Errore durante il download del file XML:", error);
      } finally {
        isLoadingGas.value = false;
      }
    };

    const formatYearForSemestrale = (value) => {
      if (value instanceof Date && !Number.isNaN(value.getTime())) {
        return value.getFullYear();
      }

      const parsed = parseInt(value, 10);
      return Number.isNaN(parsed) ? new Date().getFullYear() : parsed;
    };

    const downloadBlob = (blob, fileName) => {
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    };

    const showDichiarazioneSemestraleEE = ref(false);
    const dichiarazioneSemestraleEEYear = ref(new Date());
    const dichiarazioneSemestraleEESemestre = ref("1");
    const isLoadingSemestraleEE = ref(false);

    const showDichiarazioneSemestraleGAS = ref(false);
    const dichiarazioneSemestraleGASYear = ref(new Date());
    const dichiarazioneSemestraleGASSemestre = ref("1");
    const isLoadingSemestraleGAS = ref(false);

    const hideDeclarationSections = () => {
      showDichiarazioneEE.value = false;
      showDichiarazioneGAS.value = false;
      showDichiarazioneSemestraleEE.value = false;
      showDichiarazioneSemestraleGAS.value = false;
      showAddizionaliGas.value = false;
    };

    const hideExciseSections = () => {
      showElettricoData.value = false;
      showGasData.value = false;
      showMensileEE.value = false;
      showMensileGAS.value = false;
    };

    const resetDisplayedResults = () => {
      hideExciseSections();
      hideDeclarationSections();
    };

    const onSelectionChange = () => {
      periodTabIndex.value = null;
      operationTabIndex.value = null;
      filterYear.value = null;
      filterMonth.value = null;
      filterConsumptionPeriod.value = null;
      filterAddizionaliPeriod.value = null;
      resetDisplayedResults();
    };

    const onPeriodChange = () => {
      operationTabIndex.value = null;
      filterYear.value = null;
      filterMonth.value = null;
      filterConsumptionPeriod.value = null;
      filterAddizionaliPeriod.value = null;
      resetDisplayedResults();
    };

    const onOperationChange = () => {
      filterYear.value = null;
      filterMonth.value = null;
      filterConsumptionPeriod.value = null;
      filterAddizionaliPeriod.value = null;
      resetDisplayedResults();
    };

    const getSelectedYear = () => formatYearForSemestrale(filterYear.value);
    const getSelectedMonth = () => ({
      year: filterMonth.value.getFullYear(),
      month: String(filterMonth.value.getMonth() + 1).padStart(2, "0"),
    });

    const configureAddizionaliPeriod = () => {
      addizionaliSearchMode.value = "periodo";
      const [start, end] = filterAddizionaliPeriod.value;
      addizionaliYearFrom.value = start.getFullYear();
      addizionaliMonthFrom.value = String(start.getMonth() + 1).padStart(2, "0");
      addizionaliYearTo.value = end.getFullYear();
      addizionaliMonthTo.value = String(end.getMonth() + 1).padStart(2, "0");
    };

    const executeSearch = async () => {
      if (!canSearch.value) return;
      resetDisplayedResults();
      const commodity = selectedCommodity.value;
      const period = selectedPeriod.value;
      const operation = selectedOperation.value;

      if (operation === "addizionali") {
        if (!hasValidAddizionaliPeriod.value) return;
        showAddizionaliGas.value = true;
        dichiarazioneAddizionaliGasData.value = null;
        configureAddizionaliPeriod();
        await loadDichiarazioneAddizionaliGas();
        return;
      }

      if (period === "annuale" && operation === "accise") {
        selectedYear.value = getSelectedYear();
        if (commodity === "ee") {
          showElettricoData.value = true;
          await loadElettricoData();
        } else {
          showGasData.value = true;
          await loadGasData();
        }
        return;
      }

      if (period === "annuale" && operation === "consumi") {
        if (!hasValidConsumptionPeriod.value) return;
        const annualPeriod = [...filterConsumptionPeriod.value];
        if (commodity === "ee") {
          dichiarazionePeriod.value = annualPeriod;
          showDichiarazioneEE.value = true;
          await loadDichiarazioneData();
        } else {
          dichiarazioneGasPeriod.value = annualPeriod;
          showDichiarazioneGAS.value = true;
          await loadDichiarazioneGasData();
        }
        return;
      }

      if (period === "semestrale" && operation === "consumi") {
        if (commodity === "ee") {
          dichiarazioneSemestraleEEYear.value = filterYear.value;
          dichiarazioneSemestraleEESemestre.value = filterSemester.value;
          await downloadDichiarazioneSemestraleEEXml();
        } else {
          dichiarazioneSemestraleGASYear.value = filterYear.value;
          dichiarazioneSemestraleGASSemestre.value = filterSemester.value;
          await downloadDichiarazioneSemestraleGASXml();
        }
        return;
      }

      if (period === "mensile" && operation === "accise") {
        const { year, month } = getSelectedMonth();
        if (commodity === "ee") {
          selectedYear.value = year;
          selectedMonth.value = month;
          showMensileEE.value = true;
          await loadMensileEEData();
        } else {
          selectedYearGAS.value = year;
          selectedMonthGAS.value = month;
          showMensileGAS.value = true;
          await loadMensileGASData();
        }
      }
    };

    const semestreOptions = [
      { label: "1° Semestre", value: "1" },
      { label: "2° Semestre", value: "2" },
    ];

    const downloadDichiarazioneSemestraleEEXml = async () => {
      try {
        isLoadingSemestraleEE.value = true;
        const anno = formatYearForSemestrale(dichiarazioneSemestraleEEYear.value);
        const response = await service.getDichiarazioneXml("semestrale", "ee", {
          anno,
          semestre: dichiarazioneSemestraleEESemestre.value,
        });

        downloadBlob(
          response,
          `Dichiarazione_Semestrale_EE_${anno}_${dichiarazioneSemestraleEESemestre.value}.xml`
        );
      } catch (error) {
        console.error("Errore durante il download XML semestrale EE:", error);
      } finally {
        isLoadingSemestraleEE.value = false;
      }
    };

    const downloadDichiarazioneSemestraleGASXml = async () => {
      try {
        isLoadingSemestraleGAS.value = true;
        const anno = formatYearForSemestrale(dichiarazioneSemestraleGASYear.value);
        const response = await service.getDichiarazioneXml("semestrale", "gas", {
          anno,
          semestre: dichiarazioneSemestraleGASSemestre.value,
        });

        downloadBlob(
          response,
          `Dichiarazione_Semestrale_GAS_${anno}_${dichiarazioneSemestraleGASSemestre.value}.xml`
        );
      } catch (error) {
        console.error("Errore durante il download XML semestrale GAS:", error);
      } finally {
        isLoadingSemestraleGAS.value = false;
      }
    };

    const exportSelectedConsumptionXml = async () => {
      const commodity = selectedCommodity.value;

      if (selectedPeriod.value === "semestrale") {
        if (commodity === "ee") {
          dichiarazioneSemestraleEEYear.value = filterYear.value;
          dichiarazioneSemestraleEESemestre.value = filterSemester.value;
          await downloadDichiarazioneSemestraleEEXml();
        } else {
          dichiarazioneSemestraleGASYear.value = filterYear.value;
          dichiarazioneSemestraleGASSemestre.value = filterSemester.value;
          await downloadDichiarazioneSemestraleGASXml();
        }
        return;
      }

      if (!hasValidConsumptionPeriod.value) return;
      const annualPeriod = [...filterConsumptionPeriod.value];
      if (commodity === "ee") {
        dichiarazionePeriod.value = annualPeriod;
        await downloadDichiarazioneXml();
      } else {
        dichiarazioneGasPeriod.value = annualPeriod;
        await downloadDichiarazioneGasXml();
      }
    };

    const calculateProgressiveTotal = (months, currentMonth, data) => {
      let total = 0;
      months.forEach((month) => {
        if (parseInt(month.value) <= parseInt(currentMonth)) {
          total += data.reduce(
            (acc, curr) => acc + (curr.mesi[month.value]?.rataReale || 0),
            0
          );
        }
      });
      return total;
    };

    const getPreviousMonthAccise = (mesi, currentMonth) => {
      const currentMonthNum = parseInt(currentMonth);
      const previousMonth = (currentMonthNum - 1).toString().padStart(2, "0");

      if (currentMonthNum === 1) return 0;

      return mesi[previousMonth]?.rataReale || 0;
    };

    const getTotalConguaglio = (isProvince = false) => {
      let data;
      if (showElettricoData.value) {
        data = isProvince ? elettricoProvince.value : elettricoStatuti.value;
      } else if (showGasData.value) {
        data = isProvince ? gasProvince.value : gasStatuti.value;
      }

      if (!data) return 0;

      return data
        .filter(
          (item) =>
            item.statuto !== "Totali" && item.statuto !== "Totale Progressivo"
        )
        .reduce(
          (acc, curr) => acc + (curr.conguaglio?.acciseFatturate || 0),
          0
        );
    };

    const isEntryMonth = (provincia, month) => {
      if (!entrateUscite.value || !entrateUscite.value[provincia]) {
        return false;
      }

      const provinciaData = entrateUscite.value[provincia];
      if (!provinciaData.entry_months || !provinciaData.entry_months.length) {
        return false;
      }

      return provinciaData.entry_months.some((entry) => {
        const entryDate = new Date(entry);
        return (
          entryDate.getMonth() + 1 === parseInt(month) &&
          entryDate.getFullYear() === selectedYear.value
        );
      });
    };

    const isExitMonth = (provincia, month) => {
      if (!entrateUscite.value || !entrateUscite.value[provincia]) return false;

      const provinciaData = entrateUscite.value[provincia];
      if (!provinciaData.exit_months || !provinciaData.exit_months.length)
        return false;

      return provinciaData.exit_months.some((exit) => {
        const exitDate = new Date(exit);
        return (
          exitDate.getMonth() + 1 === parseInt(month) &&
          exitDate.getFullYear() === selectedYear.value
        );
      });
    };

    const isEntryMonthGas = (provincia, month) => {

      if (!entrateUsciteGas.value || !entrateUsciteGas.value[provincia]) {
        console.log("No gas data for province:", provincia);
        return false;
      }

      const provinciaData = entrateUsciteGas.value[provincia];

      if (!provinciaData.entry_months || !provinciaData.entry_months.length) {
        console.log("No gas entry months for province:", provincia);
        return false;
      }

      const result = provinciaData.entry_months.some((entry) => {
        const entryDate = new Date(entry);
        const match =
          entryDate.getMonth() + 1 === parseInt(month) &&
          entryDate.getFullYear() === selectedYear.value;
        return match;
      });

      return result;
    };

    const isExitMonthGas = (provincia, month) => {
      if (!entrateUsciteGas.value || !entrateUsciteGas.value[provincia])
        return false;

      const provinciaData = entrateUsciteGas.value[provincia];
      if (!provinciaData.exit_months || !provinciaData.exit_months.length)
        return false;

      return provinciaData.exit_months.some((exit) => {
        const exitDate = new Date(exit);
        return (
          exitDate.getMonth() + 1 === parseInt(month) &&
          exitDate.getFullYear() === selectedYear.value
        );
      });
    };

    const isLoadingEE = ref(false);
    const isLoadingGas = ref(false);
    const xmlExporting = computed(() =>
      selectedCommodity.value === "ee"
        ? isLoadingEE.value || isLoadingSemestraleEE.value
        : isLoadingGas.value || isLoadingSemestraleGAS.value
    );
    const searching = computed(
      () =>
        loader.value ||
        loading.value ||
        loadingGAS.value ||
        dichiarazioneLoading.value ||
        dichiarazioneGasLoading.value ||
        loadingAddizionaliGas.value ||
        isLoadingSemestraleEE.value ||
        isLoadingSemestraleGAS.value
    );


    return {
      loader,
      commodityTabIndex,
      commodityOptions,
      periodTabIndex,
      periodOptions,
      operationTabIndex,
      operationOptions,
      filterYear,
      currentYearMaxDate,
      filterSemester,
      filterMonth,
      filterConsumptionPeriod,
      consumptionMaxDate,
      hasValidConsumptionPeriod,
      filterAddizionaliPeriod,
      addizionaliMaxDate,
      hasValidAddizionaliPeriod,
      selectedPeriod,
      availableOperations,
      selectedOperation,
      canSearch,
      selectedYearsLabel,
      onSelectionChange,
      onPeriodChange,
      onOperationChange,
      executeSearch,
      searching,
      exportSelectedConsumptionXml,
      xmlExporting,
      months,
      mensileMonthOptions,
      selectedYear,
      elettricoStatuti,
      gasStatuti,
      elettricoProvince,
      gasProvince,
      formatCurrency,
      loadAllData,
      showElettrico,
      showGas,
      showElettricoData,
      showGasData,
      downloadCsv,
      showMensileEE,
      loadMensileEEData,
      fasceArray,
      totali,
      calculateQuotaPod,
      calculateQuotaConsumo,
      formatNumber,
      selectedMonth,
      showMensileEETable,
      showMensileGAS,
      showMensileGASTable,
      loadMensileGASData,
      selectedMonthGAS,
      selectedYearGAS,
      mensileGasMonthKey,
      selectedAmbitoEE,
      selectedAmbitoGAS,
      ambitoOptions,
      fasceArrayGAS,
      totaliGAS,
      loadingGAS,
      calculateQuotaPdrGAS,
      calculateQuotaConsumoGAS,
      downloadGasCsv,
      loadingDownload,
      getPreviousMonthValue,
      getPreviousMonthValueGas,
      calculateMonthlyTotal,
      exportToExcel,
      showDichiarazioneEE,
      dichiarazionePeriod,
      hasValidDichiarazionePeriod,
      dichiarazioneData,
      dichiarazioneLoading,
      loadDichiarazioneData,
      downloadDichiarazioneCsv,
      showDichiarazioneGAS,
      showDichiarazioneSemestraleEE,
      showDichiarazioneSemestraleGAS,
      dichiarazioneAddizionaliGasData,
      showAddizionaliGas,
      loadingAddizionaliGas,
      addizionaliSearchMode,
      addizionaliYear,
      addizionaliYearFrom,
      addizionaliYearTo,
      addizionaliMonthFrom,
      addizionaliMonthTo,
      addizionaliGasRows,
      exportAddizionaliGasToCsv,
      dichiarazioneGasPeriod,
      hasValidDichiarazioneGasPeriod,
      dichiarazioneGasData,
      dichiarazioneGasLoading,
      loadDichiarazioneGasData,
      openAddizionaliGasDialog,
      loadDichiarazioneAddizionaliGas,
      downloadDichiarazioneGasCsv,
      hasDichiarazioneData,
      getTotalByProvincia,
      downloadDichiarazioneXml,
      downloadDichiarazioneGasXml,
      dichiarazioneSemestraleEEYear,
      dichiarazioneSemestraleEESemestre,
      isLoadingSemestraleEE,
      dichiarazioneSemestraleGASYear,
      dichiarazioneSemestraleGASSemestre,
      isLoadingSemestraleGAS,
      semestreOptions,
      downloadDichiarazioneSemestraleEEXml,
      downloadDichiarazioneSemestraleGASXml,
      calculateProgressiveTotal,
      hasDichiarazioneGasData: computed(() => {
        const data = dichiarazioneGasData.value;
        const hasItems = (value) =>
          Array.isArray(value)
            ? value.length > 0
            : value && typeof value === "object"
              ? Object.keys(value).length > 0
              : false;

        return Boolean(
          data &&
            (hasItems(data.domesticiMezzogiorno) ||
              hasItems(data.domesticiCentroNord) ||
              hasItems(data.altriUsi) ||
              data.riepilogo != null ||
              data.totali != null)
        );
      }),
      getPreviousMonthAccise,
      getTotalConguaglio,
      isEntryMonth,
      isExitMonth,
      entrateUscite,
      isLoadingEE,
      isLoadingGas,
      isEntryMonthGas,
      isExitMonthGas,
      entrateUsciteGas,
    };
  },
};
</script>

<style scoped>
.excise-workspace {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.excise-workspace > .title {
  margin-bottom: 0.25rem;
}

.selection-row {
  display: contents;
}

.visual-filter-flow {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 1rem;
  padding: 1.5rem;
}

.selection-group {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.9rem;
  width: 14.5rem;
  min-height: 13.5rem;
  padding: 1.25rem;
  border: 2px solid #b5c7d9;
  border-radius: 10px;
  background: var(--surface-card);
  box-shadow: 0 3px 10px rgba(31, 62, 92, 0.1);
}

.selection-label {
  font-size: 1rem;
  font-weight: 700;
  text-align: center;
}

.selection-group :deep(.p-selectbutton) {
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: center;
  gap: 0.65rem;
}

.selection-group :deep(.p-selectbutton .p-button) {
  justify-content: center;
  width: 100%;
  border: 1.5px solid #aabfd4;
  border-radius: 6px !important;
}

.selection-group :deep(.p-selectbutton .p-button.p-highlight) {
  border-color: var(--primary-color);
}

.selection-arrow {
  color: var(--primary-color);
  font-size: 1.25rem;
}

.search-panel {
  display: flex;
  align-items: stretch;
  flex-direction: column;
  justify-content: center;
  gap: 1rem;
  width: 20rem;
  min-height: 13.5rem;
  margin: 0;
  padding: 1.25rem;
  border: 2px solid #b5c7d9;
  border-radius: 8px;
  background: var(--surface-ground);
}

.search-panel::before {
  content: "Intervallo";
  font-size: 1rem;
  font-weight: 700;
  text-align: center;
}

.search-panel::after {
  content: "";
  position: absolute;
}

.visual-filter-flow > .search-panel {
  position: relative;
}

.visual-filter-flow > .search-panel::marker {
  display: none;
}

.search-field {
  margin: 0;
}

.search-field > label,
.search-field > .field-label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
}

.search-button {
  min-width: 7rem;
}

.result-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.header-actions {
  display: flex;
  flex-direction: column;
  margin-bottom: 1rem;
}

.controls-wrapper {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1rem;
  overflow-x: auto;
  padding-bottom: 0.5rem;
  white-space: nowrap;
}

.controls-row,
.declarations-row {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-shrink: 0;
}

.declarations-row {
  flex-direction: column;
  align-items: flex-start;
}

.declaration-type-selector {
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--surface-border);
}

.declaration-navigation {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.declaration-navigation-label {
  color: var(--text-color-secondary);
  font-size: 0.85rem;
  font-weight: 600;
}

.inline-declaration-section {
  margin-top: 1.5rem;
  overflow: hidden;
  border: 1px solid var(--surface-border);
  border-radius: 8px;
  background: var(--surface-card);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.inline-declaration-section > .section-title {
  margin: 0;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid var(--surface-border);
  background: var(--surface-50);
  font-size: 1.25rem;
}

.declaration-content {
  padding: 1.25rem;
}

.declaration-filters {
  margin: 0 0 1.5rem;
  padding: 1rem;
  border: 1px solid var(--surface-border);
  border-radius: 6px;
  background: var(--surface-ground);
}

.declaration-fields {
  width: 100%;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.declaration-fields .period-selector {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.declaration-form {
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 1rem;
}

.declaration-form .field {
  flex: 0 1 15rem;
  margin-top: 0 !important;
}

.declaration-form label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
}

.field-label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
}

.semester-field {
  flex-basis: auto !important;
}

.semester-options,
.semester-option {
  display: flex;
  align-items: center;
}

.semester-options {
  min-height: 2.357rem;
  gap: 1.25rem;
}

.semester-option {
  gap: 0.5rem;
}

.declaration-form .semester-option label {
  margin: 0;
  white-space: nowrap;
}

.declaration-actions {
  display: flex;
  flex-direction: column;
}

.declaration-action-label {
  margin-bottom: 0.5rem;
  font-weight: 600;
  visibility: hidden;
}

@media (max-width: 768px) {
  .visual-filter-flow {
    display: flex;
    flex-direction: column;
  }

  .selection-group {
    width: auto;
    min-height: auto;
  }

  .selection-arrow {
    align-self: center;
    transform: rotate(90deg);
  }

  .search-panel {
    width: 100%;
    min-height: auto;
  }

  .search-panel {
    align-items: stretch;
    flex-direction: column;
  }

  .declaration-form {
    align-items: stretch;
    flex-direction: column;
  }

  .declaration-form .field {
    flex-basis: auto;
  }
}

.conguaglio-cell {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  text-align: right;
}

.conguaglio-label {
  font-size: 0.75rem;
  font-weight: bold;
  color: #555;
}

.conguaglio-value {
  font-size: 1rem;
  font-weight: bold;
  color: #000;
}

.year-selector {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  flex-shrink: 0;
}

.button-group {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  flex-shrink: 0;
}

.controls-container {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-shrink: 0;
}

.controls {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.divider {
  width: 2px;
  height: 40px;
  background-color: #dee2e6;
  flex-shrink: 0;
}

.controls-wrapper::-webkit-scrollbar {
  height: 4px;
}

.controls-wrapper::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.controls-wrapper::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.controls-wrapper::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.section-title {
  margin-top: 2rem;
  margin-bottom: 1rem;
  font-size: 1.5rem;
  font-weight: bold;
}

.month-cell {
  padding: 0.25rem;
  border: 1px solid #ddd;
  border-radius: 2px;
  background-color: #f9f9f9;
  margin-bottom: 0.25rem;
  font-size: 0.9rem;
}

.data-row {
  display: flex;
  justify-content: space-between;
  width: 100%;
  font-size: 1rem;
  color: #333;
}

.label {
  font-size: 0.85rem;
}

.value {
  font-size: 0.85rem;
}

.total-row {
  font-weight: bold;
}

:deep(.month-data) {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

:deep(.data-row) {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: 0.2rem;
}

:deep(.label) {
  font-size: 0.65rem;
  font-weight: bold;
}

:deep(.value) {
  font-size: 0.9rem;
  font-weight: bold;
  margin-top: 0.2rem;
}

:deep(.p-datatable-thead > tr > th),
:deep(.p-datatable-tbody > tr > td) {
  min-width: 200px !important;
  max-width: 250px;
}

:deep(.p-datatable) {
  border: 1px solid #ddd;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  background-color: #ffffff;
  font-size: 0.7rem;
}

:deep(.p-datatable-header) {
  background-color: #eceaea;
  padding: 0.4rem !important;
  font-size: 0.7rem;
  min-width: 75px !important;
  max-width: 85px !important;
  text-align: flex-start;
}

:deep(.p-datatable-thead > tr > th) {
  background-color: #3388b3;
  color: #ffffff;
  font-size: 1.2em;
  min-width: 60px !important;
  max-width: 70px !important;
  text-align: flex-start;
}

:deep(.p-datatable-tbody > tr) {
  transition: background-color 0.2s ease, transform 0.2s ease;
  height: 1rem;
}

:deep(.p-datatable-tbody > tr:hover) {
  background-color: var(--blue-100);
  font-weight: bold !important;
}

:deep(.p-datatable-tbody > tr > td) {
  padding: 0.3rem !important;
  border-bottom: 1px solid #eee;
  color: #333;
  padding: 0.3rem !important;
  font-size: 0.7rem;
  text-align: flex-start;
  min-width: 75px !important;
  white-space: nowrap;
}

:deep(.p-datatable-tbody > tr > td:has(.value-green)) {
  background-color: #689f38cf;
  color: white;
  font-weight: bold;
  border-radius: 4px;
}

:deep(.p-datatable-tbody > tr > td:has(.value-red)) {
  background-color: #9f2c2c;
  color: white;
  border-radius: 4px;
}

:deep(.p-datatable-tbody > tr > td:has(.value-green) .label) {
  color: white;
}

:deep(.p-datatable-tbody > tr > td:has(.value-red) .label) {
  color: white;
}

:deep(.p-datatable-tbody > tr.total-row) > td {
  background-color: #e9db63 !important;
  color: #000 !important;
  font-weight: bold !important;
}

:deep(.p-datatable-tbody > tr > td:first-child) {
  background-color: #f7f7f7;
}

:deep(.p-datatable-tbody > tr:last-child > td) {
  border-bottom: none;
}

:deep(.p-datatable-wrapper) {
  overflow-x: hidden !important;
  max-width: 100%;
}

.statuto-cell {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  font-size: 0.9rem;
  color: #000000;
}

.month-data {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.5rem;
  position: relative;
  min-height: 4rem;
}

.value-green {
  font-weight: bold;
  color: #ffffff;
  font-size: 1rem;
}

.value-red {
  font-weight: bold;
  color: #ffffff;
}

.download-btn {
  display: flex;
  font-size: 0.7rem;
  align-items: center;
  justify-content: center;
  padding: 0.3rem 0.5rem;
  font-size: 0.5rem;
  border: none;
  border-radius: 50px;
  background-color: #315058;
  color: #ffffff;
  cursor: pointer;
  text-align: center;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  max-width: 80px;
  bottom: 1px;
  right: 2px;
}

.download-btn:hover {
  background-color: #3388b3 !important;
  color: white !important;
}

.mensile-ee-container {
  padding: 1rem;
}

.header-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.select-container {
  display: flex;
  gap: 1rem;
  align-items: center;
}

.total-row {
  font-weight: bold;
}

.total-row strong {
  color: #000;
}

.totals {
  margin-top: 1rem;
  padding: 1rem;
  background-color: #f8f9fa;
  border-radius: 4px;
}

.totals-row {
  display: flex;
  background-color: #f8f9fa;
  border-top: 2px solid #dee2e6;
  padding: 0.5rem;
  margin-top: 0.5rem;
}

.totals-cell {
  flex: 1;
  min-width: 150px;
  padding: 0.5rem;
  border-right: 1px solid #dee2e6;
}

.totals-cell:first-child {
  min-width: 200px;
}

.totals-cell:last-child {
  border-right: none;
}

:deep(.data-row .label-white) {
  color: white !important;
}

:deep(.p-datatable-footer) {
  background-color: #f8f9fa;
  font-weight: bold;
}

:deep(.p-datatable-footer td) {
  border-right: 1px solid #dee2e6;
}

:deep(.p-datatable-footer td:last-child) {
  border-right: none;
}

:deep(.p-datatable-wrapper) {
  overflow-x: auto;
}

:deep(.p-datatable-footer) {
  background-color: #f8f9fa;
  position: sticky;
  bottom: 0;
  z-index: 1;
}

:deep(.p-datatable-footer td) {
  white-space: nowrap;
  border-right: 1px solid #dee2e6;
  background-color: #f8f9fa;
}

:deep(.p-datatable-footer td:last-child) {
  border-right: none;
}

:deep(.p-datatable-footer td),
:deep(.p-datatable-thead th) {
  flex: 1 0 auto;
  width: auto;
}

.p-button-success {
  margin-left: 1rem;
}

.dichiarazione-ee-container {
  padding: 1rem;
}

.dichiarazione-ee-container .header-actions {
  margin-bottom: 2rem;
}

.dichiarazione-ee-container h3 {
  margin-top: 2rem;
  margin-bottom: 1rem;
}

.dichiarazione-ee-container h3:first-child {
  margin-top: 0;
}

.totali-table {
  width: 100%;
  margin: 1rem 0;
  border-collapse: collapse;
}

.totali-table .p-datatable {
  width: 100% !important;
  overflow-x: visible !important;
}

.totali-table .p-datatable-wrapper {
  overflow-x: visible !important;
}

.totali-table .p-datatable-table {
  width: 100% !important;
  table-layout: fixed;
}

.totali-table .p-datatable-tbody > tr > td,
.totali-table .p-datatable-thead > tr > th {
  padding: 0.75rem 1rem;
  text-align: right;
  border: 1px solid #dee2e6;
  width: auto !important;
}

.totali-table .p-datatable-thead > tr > th {
  background-color: #f8f9fa;
  font-weight: bold;
}

.totali-table .p-datatable-tbody > tr > td:last-child,
.totali-table .p-datatable-thead > tr > th:last-child {
  background-color: #e9ecef;
  font-weight: bold;
  border-left: 2px solid #dee2e6;
}

.totali-table .p-column {
  flex: 1;
  min-width: 150px;
}

.loading-indicator {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
}

.totali-table p-grid .font-bold {
  font-weight: bold;
}

.colonna-complessivo {
  display: table-cell !important;
  visibility: visible !important;
  width: auto !important;
  min-width: 150px !important;
  overflow: visible !important;
}

.p-datatable .p-datatable-tbody > tr > td.colonna-complessivo {
  display: table-cell !important;
  visibility: visible !important;
  overflow: visible !important;
}

.region-block {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.region-row {
  font-weight: 700;
}

.province-row {
  margin-left: 1rem;
  color: #555;
}
</style>

